<template>
  <div class="chat-container" :class="{ 'mobile-chat': $vuetify.breakpoint.xs }">
    <!-- Group Info Sidebar - Place at root level of chat-container -->
    <group-info-sidebar v-model="groupInfoDrawer" :chat-id="singleRoom?._id" @left-group="handleLeftGroup"
      @show-snackbar="showSnackbar" />

    <!-- Chat Header - Fixed -->
    <div class="chat-header" v-if="singleRoom">
      <div class="header-content" @click="goToProfile" v-if="singleRoom && (isGroupChat || otherUser)">
        <div class="back-button" v-if="$vuetify.breakpoint.xs" @click.stop="$emit('back')">
          <v-icon color="white">mdi-arrow-left</v-icon>
        </div>
        <div class="user-avatar">
          <editable-avatar :image="chatHeaderAvatar" :size="$vuetify.breakpoint.xs ? 35 : 40" :editable="false" />
        </div>

        <div class="user-info">
          <div class="user-name">{{ chatHeaderName }}</div>
          <div class="user-status">{{ chatHeaderStatus }}</div>
        </div>
      </div>

      <div class="header-actions" v-if="hasMessages">
        <v-btn icon color="white" @click="toggleSearch" v-if="!$vuetify.breakpoint.xs">
          <v-icon>mdi-magnify</v-icon>
        </v-btn>
        <v-menu bottom left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon color="white" v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="viewContact" v-if="!isGroupChat">
              <v-list-item-icon><v-icon>mdi-account</v-icon></v-list-item-icon>
              <v-list-item-title>View contact</v-list-item-title>
            </v-list-item>
            <v-list-item @click="clearChatMessages" :disabled="clearingChat">
              <v-list-item-icon>
                <v-progress-circular v-if="clearingChat" indeterminate size="20" width="2" color="error" />
                <v-icon v-else>mdi-delete</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ clearingChat ? "Clearing..." : "Clear chat" }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- Search Bar - Fixed below header -->
    <div class="search-bar" v-if="showSearch">
      <div class="search-bar-content">
        <div class="search-input-wrapper">
          <v-icon class="search-icon" size="20" color="#54656f">mdi-magnify</v-icon>
          <input v-model="searchQuery" placeholder="Search messages..." @input="searchMessages"
            @keydown.escape="closeSearch" class="search-input-field" ref="searchInput" autofocus />
          <v-btn v-if="searchQuery" icon x-small @click="clearSearch" class="clear-search-btn">
            <v-icon size="16" color="#54656f">mdi-close-circle</v-icon>
          </v-btn>
        </div>
        <v-btn icon small @click="closeSearch" class="search-close-btn"
          :class="{ 'search-close-btn-mobile': $vuetify.breakpoint.xs }">
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </div>
      <div v-if="searchQuery" class="search-results-info">
        <span v-if="filteredMessages.length > 0" class="results-count">
          <v-icon size="14" color="#667781" class="mr-1">mdi-message-text</v-icon>
          {{ filteredMessages.length }} message{{ filteredMessages.length !== 1 ? 's' : '' }} found
        </span>
        <span v-else class="results-count no-results">
          <v-icon size="14" color="#667781" class="mr-1">mdi-alert-circle-outline</v-icon>
          No messages found
        </span>
      </div>
    </div>

    <!-- Messages Container - Scrollable -->
    <div class="messages-container" ref="messagesContainer" @scroll="onScroll">
      <div v-if="!singleRoom || !singleRoom.messages || singleRoom.messages.length === 0" class="empty-messages">
        <no-messages :is-group-chat="isGroupChat" @start-conversation="startNewConversation" />
      </div>

      <div v-else class="messages-wrapper">
        <!-- PINNED MESSAGES BANNER -->
        <div v-if="pinnedMessages.length > 0" class="pinned-messages-banner">
          <div class="pinned-header">
            <v-icon size="16" color="#f57c00">mdi-pin</v-icon>
            <span class="pinned-title">Pinned Messages</span>
            <span class="pinned-count">({{ pinnedMessages.length }})</span>
            <v-spacer></v-spacer>
            <v-btn icon x-small @click="scrollToPinnedMessage(pinnedMessages[pinnedMessages.length - 1])"
              class="pinned-nav-btn" title="Scroll to latest pinned">
              <v-icon size="16">mdi-chevron-down</v-icon>
            </v-btn>
          </div>
          <div class="pinned-messages-list">
            <div v-for="(pinnedMsg) in pinnedMessages" :key="pinnedMsg._id" class="pinned-message-item"
              @click="scrollToPinnedMessage(pinnedMsg)">
              <div class="pinned-message-content">
                <div class="pinned-message-sender">
                  <span class="pinned-sender-name">{{ getPinnedMessageSender(pinnedMsg) }}</span>
                  <span class="pinned-message-time">{{ formatMessageTime(pinnedMsg.createdAt || pinnedMsg.date ||
                    pinnedMsg.timestamp) }}</span>
                </div>
                <div class="pinned-message-text">
                  {{ getPinnedMessagePreview(pinnedMsg) }}
                </div>
              </div>
              <v-btn icon x-small @click.stop="togglePinMessage(pinnedMsg)" class="unpin-btn" title="Unpin message">
                <v-icon size="14" color="#999">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Regular messages -->
        <div v-for="(group, date) in groupedMessages" :key="date" class="date-group">
          <div class="date-separator">
            <span class="date-text">{{ formatDate(date) }}</span>
          </div>

          <div v-for="message in group" :key="message._id || message.clientMessageId || Date.now() + Math.random()"
            :data-message-id="message._id || message.clientMessageId" class="message-wrapper" :class="[
              getMessageClasses(message),
              { 'message-pinned': message.isPinned },
              { 'message-system': isSystemMessage(message) },
              { 'message-group': isGroupChat }
            ]" @contextmenu.prevent="handleMessageLongPress(message, $event)">

            <!-- System Message - Centered -->
            <div v-if="isSystemMessage(message)" class="system-message">
              <span class="system-message-text">
                {{ getSystemMessageText(message) }}
              </span>
              <span class="system-message-time">{{ formatMessageTime(message.createdAt || message.date ||
                message.timestamp)
              }}</span>
            </div>

            <!-- Regular Message -->
            <div v-else class="message-bubble" :class="getMessageBubbleClasses(message)">
              <!-- Add sender name for group chats -->
              <div v-if="isGroupChat && !isOwnMessage(message)" class="message-sender-name">
                {{ getSenderName(message) }}
              </div>
              <!-- Add sender avatar for group chats -->
              <div v-if="isGroupChat && !isOwnMessage(message)" class="message-sender-avatar">
                <v-avatar size="28" class="sender-avatar">
                  <img :src="getSenderAvatar(message)" :alt="getSenderName(message)" />
                </v-avatar>
              </div>

              <div class="message-content-container">
                <!-- Pinned Badge on Message -->
                <div class="pinned-badge" v-if="message.isPinned">
                  <v-icon size="12" color="#f57c00">mdi-pin</v-icon>
                  <span class="pinned-badge-text">Pinned</span>
                </div>
                <!-- Message Actions - Three Dots Menu -->
                <div class="message-actions">
                  <v-menu v-model="messageMenu[message._id || message.clientMessageId]" :close-on-content-click="true"
                    :offset-y="!$vuetify.breakpoint.xs" :nudge-width="200" :absolute="!$vuetify.breakpoint.xs">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon x-small v-bind="attrs" v-on="on" class="action-btn"
                        @click.stop.prevent="openMessageMenu(message, $event)"
                        :class="{ 'action-btn-mobile': $vuetify.breakpoint.xs }">
                        <v-icon small>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense class="action-menu">
                      <!-- Reply -->
                      <v-list-item @click="replyToMessage(message)">
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="#4CAF50">mdi-reply</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="action-item">Reply</v-list-item-title>
                      </v-list-item>

                      <!-- Pin/Unpin Message -->
                      <v-list-item @click="togglePinMessage(message)">
                        <v-list-item-icon class="mr-2">
                          <v-icon small :color="message.isPinned ? 'orange' : ''">
                            {{ message.isPinned ? 'mdi-pin' : 'mdi-pin-outline' }}
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="action-item">
                          {{ message.isPinned ? 'Unpin message' : 'Pin message' }}
                        </v-list-item-title>
                      </v-list-item>

                      <!-- Edit - Only for own messages within 15 mins -->
                      <v-list-item v-if="canEditMessage(message)" @click="editMessage(message)">
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="primary">mdi-pencil-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="action-item">Edit</v-list-item-title>
                      </v-list-item>

                      <!-- Copy -->
                      <v-list-item v-if="message.text || message.message" @click="copyMessage(message)">
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="grey darken-1">mdi-content-copy</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="action-item">Copy</v-list-item-title>
                      </v-list-item>

                      <!-- Delete -->
                      <template v-if="isOwnMessage(message)">
                        <v-list-item @click="deleteMessage(message)">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="error">mdi-delete-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title class="action-item error--text">Delete for me</v-list-item-title>
                        </v-list-item>
                        <v-list-item v-if="canDeleteForEveryoneMessage(message)"
                          @click="deleteMessageForEveryoneAction(message)">
                          <v-list-item-icon class="mr-2">
                            <v-icon small color="error">mdi-delete-forever</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title class="action-item error--text">Delete for everyone</v-list-item-title>
                        </v-list-item>
                      </template>
                      <v-list-item v-else @click="deleteMessage(message)">
                        <v-list-item-icon class="mr-2">
                          <v-icon small color="error">mdi-delete-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="action-item error--text">Delete for me</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>

                <!-- Reply Preview -->
                <div class="reply-preview" v-if="message.replyTo && message.replyTo.messageId">
                  <div class="reply-indicator">
                    <v-icon size="14" color="#128C7E">mdi-reply</v-icon>
                    <span class="reply-sender">{{ getReplySenderName(message.replyTo) }}</span>
                  </div>
                  <div class="reply-content">{{ getReplyPreview(message.replyTo) }}</div>
                </div>

                <!-- Images -->
                <div class="message-image" v-if="getMessageImages(message).length > 0">
                  <div class="message-image-grid" :class="getImageGridClass(getMessageImages(message).length)">
                    <v-img v-for="(imageUrl, idx) in getMessageImages(message).slice(0, 4)"
                      :key="`${message._id || idx}-img-${idx}`" :src="imageUrl" class="rounded-lg message-img"
                      :class="getImageClass(getMessageImages(message).length, idx)" contain
                      @click="openImagePreview(imageUrl, message)" @error="onImageError">
                      <template v-slot:placeholder>
                        <v-row class="fill-height ma-0" align="center" justify="center">
                          <v-progress-circular indeterminate color="grey lighten-5" />
                        </v-row>
                      </template>
                    </v-img>
                    <div v-if="getMessageImages(message).length > 4" class="image-count-overlay"
                      @click="openImagePreview(getMessageImages(message)[0], message)">
                      +{{ getMessageImages(message).length - 3 }}
                    </div>
                  </div>
                </div>
                <!-- Files (PDF, Documents, etc.) -->
                <div class="message-files" v-if="getMessageFiles(message).length > 0">
                  <div v-for="(file, idx) in getMessageFiles(message)" :key="`${message._id || idx}-file-${idx}`"
                    class="file-attachment" @click="openFile(file)">
                    <div class="file-icon-wrapper" :style="{ backgroundColor: getFileColor(file) + '15' }">
                      <v-icon :color="getFileColor(file)" size="32">{{ getFileIcon(file) }}</v-icon>
                    </div>
                    <div class="file-info">
                      <div class="file-name">{{ getFileName(file) }}</div>
                      <div class="file-meta">
                        <span class="file-size">{{ getFileSize(file) }}</span>
                        <span class="file-type">{{ file.type || 'File' }}</span>
                      </div>
                    </div>
                    <v-btn icon small class="download-file-btn" @click.stop="downloadFile(file)">
                      <v-icon size="18" color="primary">mdi-download</v-icon>
                    </v-btn>
                  </div>
                </div>


                <!-- ====== YOUTUBE VIDEO PREVIEW - ADD HERE ====== -->
                <div v-if="hasYouTubeVideo(message)" class="youtube-preview"
                  @click="openYouTubeVideo(getYouTubeVideoId(message))">
                  <div class="youtube-thumbnail-wrapper">
                    <v-img :src="getYouTubeThumbnail(getYouTubeVideoId(message))" class="youtube-thumbnail" height="180"
                      cover :loading="true">
                      <template v-slot:placeholder>
                        <div class="youtube-placeholder">
                          <v-progress-circular indeterminate color="red" size="40" />
                        </div>
                      </template>

                      <!-- Play Button Overlay -->
                      <div class="youtube-play-overlay">
                        <div class="play-button">
                          <v-icon size="48" color="white">mdi-play-circle</v-icon>
                        </div>
                      </div>

                      <!-- Video Duration Badge (if available) -->
                      <div class="youtube-duration-badge" v-if="message.youtubeDuration">
                        {{ formatYouTubeDuration(message.youtubeDuration) }}
                      </div>
                    </v-img>
                  </div>

                  <div class="youtube-info">
                    <div class="youtube-title">{{ getYouTubeTitle(message) }}</div>
                    <div class="youtube-meta">
                      <v-icon size="14" color="#FF0000">mdi-youtube</v-icon>
                      <span class="youtube-platform">YouTube</span>
                      <span class="youtube-separator">•</span>
                      <span class="youtube-sender">{{ getSenderName(message) }}</span>
                      <span class="youtube-separator">•</span>
                      <span class="youtube-time">{{ formatMessageTime(message.createdAt || message.date ||
                        message.timestamp) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Regular Text (only show if there's text beyond the YouTube URL) -->
                <div class="message-text" v-if="message.text || message.message">
                  <!-- Check if there's text beyond the YouTube URL -->
                  <div v-if="!hasYouTubeVideo(message) || getTextWithoutYouTubeLink(message)">
                    <div v-html="linkify(getTextWithoutYouTubeLink(message) || message.text || message.message)"></div>
                  </div>
                  <div v-if="message.edited && message.edited.isEdited" class="message-edited">(Edited)</div>
                </div>
                <!-- Text -->
                <div class="message-text" v-if="message.text || message.message">
                  <div v-html="linkify(message.text || message.message)"></div>
                  <div v-if="message.edited && message.edited.isEdited" class="message-edited">(Edited)</div>
                </div>

                <!-- Reactions -->
                <div class="message-reactions" v-if="message.reactions && getUniqueReactions(message).length > 0">
                  <div v-for="reaction in getUniqueReactions(message)" :key="reaction.emoji" class="reaction-pill"
                    @click="toggleReaction(message, reaction.emoji)" :class="{
                      'reaction-self': hasUserReacted(message, reaction.emoji),
                      'reaction-loading': reactionLoading[message._id || message.clientMessageId]
                    }" :title="getReactionTooltip(message, reaction.emoji)">
                    <span class="reaction-emoji">{{ reaction.emoji }}</span>
                    <span class="reaction-count">{{ reaction.count }}</span>
                  </div>

                  <!-- Add Reaction Button -->
                  <v-menu bottom left offset-y v-model="reactionMenu[message._id || message.clientMessageId]"
                    :close-on-content-click="false"
                    :content-class="$vuetify.breakpoint.xs ? 'reaction-picker-mobile' : 'reaction-picker-desktop'">
                    <template v-slot:activator="{ on, attrs }">
                      <div class="reaction-pill add-reaction-pill" v-bind="attrs" v-on="on" @click.stop :class="{
                        'reaction-loading': reactionLoading[message._id || message.clientMessageId],
                        'add-reaction-pill-mobile': $vuetify.breakpoint.xs
                      }">
                        <v-icon size="14" color="#666">mdi-emoticon-happy-outline</v-icon>
                      </div>
                    </template>
                    <v-card class="reaction-picker-card">
                      <div class="reaction-grid">
                        <span v-for="emoji in reactionEmojis" :key="emoji" class="reaction-emoji-option"
                          @click="toggleReaction(message, emoji)"
                          :class="{ 'reaction-selected': hasUserReacted(message, emoji) }">
                          {{ emoji }}
                        </span>
                        <span class="reaction-emoji-option more-option" @click="openFullEmojiPicker(message)">
                          <v-icon size="24" color="#666">mdi-plus</v-icon>
                        </span>
                      </div>
                    </v-card>
                  </v-menu>
                </div>

                <!-- If no reactions -->
                <div v-else class="message-reactions empty-reactions">
                  <v-menu bottom left offset-y v-model="reactionMenu[message._id || message.clientMessageId]"
                    :close-on-content-click="false"
                    :content-class="$vuetify.breakpoint.xs ? 'reaction-picker-mobile' : 'reaction-picker-desktop'">
                    <template v-slot:activator="{ on, attrs }">
                      <div class="reaction-pill add-reaction-pill" v-bind="attrs" v-on="on" @click.stop
                        :class="{ 'add-reaction-pill-mobile': $vuetify.breakpoint.xs }">
                        <v-icon size="12" color="#999">mdi-emoticon-happy-outline</v-icon>
                      </div>
                    </template>
                    <v-card class="reaction-picker-card">
                      <div class="reaction-grid">
                        <span v-for="emoji in reactionEmojis" :key="emoji" class="reaction-emoji-option"
                          @click="toggleReaction(message, emoji)"
                          :class="{ 'reaction-selected': hasUserReacted(message, emoji) }">
                          {{ emoji }}
                        </span>
                        <span class="reaction-emoji-option more-option" @click="openFullEmojiPicker(message)">
                          <v-icon size="24" color="#666">mdi-plus</v-icon>
                        </span>
                      </div>
                    </v-card>
                  </v-menu>
                </div>

                <!-- Meta -->
                <div class="message-meta">
                  <span class="message-time">{{ formatMessageTime(message.createdAt || message.date ||
                    message.timestamp) }}</span>
                  <div class="message-status" v-if="isOwnMessage(message)">
                    <v-progress-circular v-if="message.sending" indeterminate size="14" width="2" color="grey"
                      class="status-icon" />
                    <v-icon v-else-if="message.failed" size="14" color="error" class="status-icon error-icon"
                      @click="retryMessage(message)" title="Click to retry">
                      mdi-alert-circle
                    </v-icon>
                    <template v-else>
                      <v-icon :color="getMessageStatusColor(message)" size="14" class="status-icon"
                        :title="getMessageStatusTooltip(message)">
                        {{ getMessageStatusIcon(message) }}
                      </v-icon>
                    </template>
                  </div>
                  <div v-if="message.failed" class="failed-indicator" @click="retryMessage(message)">
                    <span class="retry-text">Failed - Tap to retry</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <v-btn fab small color="primary" class="scroll-bottom-btn" v-if="showScrollButton && hasMessages"
        @click="scrollToBottom">
        <v-icon>mdi-chevron-down</v-icon>
      </v-btn>
    </div>

    <!-- Typing Indicator -->
    <div class="fixed-typing-indicator" v-if="isUserTyping">
      <div class="typing-content">
        <div class="typing-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
        <span class="typing-text">{{ typingDisplayName }} is typing...</span>
      </div>
    </div>
    <div v-if="isAnyoneTyping" class="fixed-typing-indicator">
      <div class="typing-content">
        <div class="typing-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
        <span class="typing-text">{{ typingText }}</span>
      </div>
    </div>

    <!-- Reply Bar -->
    <div class="reply-bar" v-if="replyToMessageData">
      <div class="reply-bar-content">
        <div class="reply-info">
          <v-icon size="16" color="#128C7E">mdi-reply</v-icon>
          <span class="reply-label">Replying to {{ getReplySenderName(replyToMessageData) }}</span>
        </div>
        <div class="reply-preview-text">{{ getReplyPreview(replyToMessageData) }}</div>
        <v-btn icon small @click="clearReply" class="cancel-reply-btn">
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Message Input -->
    <div class="message-input-section" v-if="singleRoom">
      <!-- Attachment Preview -->
      <div class="attachment-preview" v-if="selectedImages.length > 0 || selectedFiles.length > 0">
        <div class="preview-header">
          <span class="preview-count">{{ selectedFiles.length }} file(s) selected</span>
          <v-btn icon small @click="removeAllAttachments" class="clear-all-btn">
            <v-icon size="16" color="#666">mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="preview-grid">
          <div v-for="(image, index) in selectedImages" :key="image.id" class="preview-item">
            <div class="file-preview-wrapper">
              <v-img v-if="image.type === 'image'" :src="image.preview" class="preview-image" max-height="80"
                max-width="80" contain />
              <div v-else-if="image.type === 'video'" class="file-type-icon video-icon">
                <v-icon size="32" color="#FF5722">mdi-video</v-icon>
                <div class="file-name">{{ image.name }}</div>
              </div>
              <div v-else class="file-type-icon document-icon">
                <v-icon size="32" color="#2196F3">mdi-file-document</v-icon>
                <div class="file-name">{{ image.name }}</div>
              </div>
              <div class="file-size">{{ formatFileSize(image.size) }}</div>
            </div>
            <v-btn icon x-small @click="removeAttachment(index)" class="remove-single-btn">
              <v-icon size="12" color="#fff">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
        <div v-if="selectedFiles.length < maxFiles" class="add-more-container">
          <v-btn text small color="primary" @click="triggerGallery" class="add-more-btn">
            <v-icon left size="16">mdi-plus</v-icon>
            Add more ({{ maxFiles - selectedFiles.length }} remaining)
          </v-btn>
        </div>
      </div>

      <!-- Input Row -->
      <div class="input-row">
        <div class="text-input-container">
          <v-menu top offset-y v-model="attachmentMenu" :close-on-content-click="false">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon color="#128C7E" v-bind="attrs" v-on="on" class="attachment-btn">
                <v-icon>mdi-paperclip</v-icon>
              </v-btn>
            </template>
            <v-card class="attachment-options">
              <v-list dense>
                <v-list-item @click="triggerCamera">
                  <v-list-item-icon><v-icon color="#FF5722">mdi-camera</v-icon></v-list-item-icon>
                  <v-list-item-title>Camera</v-list-item-title>
                </v-list-item>
                <v-list-item @click="triggerGallery">
                  <v-list-item-icon><v-icon color="#9C27B0">mdi-image</v-icon></v-list-item-icon>
                  <v-list-item-title>Gallery</v-list-item-title>
                </v-list-item>
                <v-list-item @click="triggerDocument">
                  <v-list-item-icon><v-icon color="#2196F3">mdi-file</v-icon></v-list-item-icon>
                  <v-list-item-title>Document</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>

          <v-textarea v-model="newMessage" :placeholder="replyToMessageData ? 'Reply...' : 'Type a message'" outlined
            dense hide-details rows="1" auto-grow max-rows="5" class="message-input"
            @keydown.enter.exact.prevent="sendMessage" @keydown.shift.enter.prevent="addNewLine" @input="onTyping"
            ref="messageInput" />

          <v-menu top offset-y max-height="400" v-model="emojiMenu" :close-on-content-click="false">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" class="emoji-btn">
                <v-icon color="#7A7A73">mdi-emoticon-happy</v-icon>
              </v-btn>
            </template>
            <v-card class="emoji-picker">
              <VEmojiPicker @select="selectEmoji" />
            </v-card>
          </v-menu>

          <v-btn icon :color="newMessage.trim() || selectedFiles.length > 0 ? '#128C7E' : '#757575'" class="send-btn"
            @click="newMessage.trim() || selectedFiles.length > 0 ? sendMessage() : startVoiceMessage()"
            :loading="sendingMessage">
            <v-icon>{{ newMessage.trim() || selectedFiles.length > 0 ? "mdi-send" : "mdi-microphone" }}</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Hidden file inputs -->
      <input type="file" ref="fileInput" style="display: none" accept="image/*" multiple @change="onFileSelected" />
      <input type="file" ref="cameraInput" style="display: none" accept="image/*" capture="camera"
        @change="onFileSelected" />
      <input type="file" ref="documentInput" style="display: none" @change="onFileSelected" />
      <input type="file" ref="videoInput" style="display: none" accept="video/*" multiple @change="onFileSelected" />
    </div>

    <!-- Image Preview Dialog -->
    <v-dialog v-model="imagePreviewDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <div class="image-preview-wrapper">
        <div class="preview-toolbar">
          <v-btn icon dark @click="closeImagePreview" class="close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <div class="preview-counter" v-if="previewImages.length > 1">
            <span class="counter-text">{{ currentPreviewImageIndex + 1 }} / {{ previewImages.length }}</span>
          </div>
          <div class="spacer"></div>
          <v-btn icon dark @click="downloadImage" class="action-btn">
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </div>

        <div class="image-container" @keydown="handlePreviewKeydown" tabindex="0" ref="imageContainer">
          <v-img ref="previewImage" :src="currentPreviewImage" contain class="preview-image"
            @click.stop="handleImageClick">
            <template v-slot:placeholder>
              <div class="image-loading">
                <v-progress-circular indeterminate color="white" />
              </div>
            </template>
          </v-img>

          <div class="navigation-overlay">
            <v-btn v-if="previewImages.length > 1 && currentPreviewImageIndex > 0" icon dark
              @click="previousPreviewImage" class="nav-overlay-btn nav-left">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn v-if="previewImages.length > 1 && currentPreviewImageIndex < previewImages.length - 1" icon dark
              @click="nextPreviewImage" class="nav-overlay-btn nav-right">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="preview-thumbnails" v-if="previewImages.length > 1">
          <div class="thumbnails-container">
            <div v-for="(image, index) in previewImages" :key="`preview-thumb-${index}`"
              :class="['thumbnail-item', { active: index === currentPreviewImageIndex }]"
              @click="goToPreviewImage(index)">
              <v-img :src="image" class="thumbnail-img" height="50" width="50" cover />
              <div class="thumbnail-overlay" v-if="index === currentPreviewImageIndex">
                <v-icon size="16" color="white">mdi-check</v-icon>
              </div>
            </div>
          </div>
        </div>

        <div class="keyboard-hints" v-if="previewImages.length > 1">
          <div class="hint-item">← → Navigate</div>
          <div class="hint-item">ESC Close</div>
          <div class="hint-item">Space Download</div>
        </div>
      </div>
    </v-dialog>

    <!-- Clear Chat Dialog -->
    <v-dialog v-model="clearChatDialog" max-width="400" persistent>
      <v-card class="rounded-xl">
        <v-card-text class="pt-6 pb-3 text-center">
          <v-icon color="red" size="64" class="mb-2">mdi-alert-circle-outline</v-icon>
          <h2 class="mb-2 text-h5 font-weight-bold">Clear All Messages?</h2>
          <p class="text-body-1 grey--text">
            This will delete all messages in this chat.
            <span v-if="isGroupChat && isGroupAdmin">You can clear for everyone or just for yourself.</span>
            <span v-else>This action cannot be undone.</span>
          </p>
        </v-card-text>

        <div v-if="isGroupChat && isGroupAdmin" class="px-4 pb-2">
          <v-checkbox v-model="clearForEveryone" label="Clear for everyone (all participants)" color="red"
            hide-details />
          <p v-if="clearForEveryone" class="caption red--text">
            ⚠️ This will delete all messages for all participants. This action cannot be undone.
          </p>
        </div>

        <div class="px-4 pb-3">
          <v-btn block large depressed color="red" dark class="mb-3 rounded-pill" @click="confirmClearChat"
            :loading="clearingChat">
            <v-icon left>mdi-broom</v-icon>
            {{ clearForEveryone ? 'Clear for Everyone' : 'Clear Chat' }}
          </v-btn>
        </div>
        <v-card-actions class="px-0 pt-0 pb-4">
          <v-btn text block large class="rounded-lg text-capitalize grey--text text--darken-1"
            @click="clearChatDialog = false">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Message Dialog -->
    <v-dialog v-model="deleteMessageDialog" max-width="400" persistent>
      <v-card class="rounded-xl">
        <v-card-text class="pt-6 pb-3 text-center">
          <v-icon color="error" size="64" class="mb-2">mdi-delete-forever</v-icon>
          <h2 class="mb-2 text-h5 font-weight-bold">
            {{ deleteAction === 'everyone' ? 'Delete for Everyone?' : 'Delete Message?' }}
          </h2>
          <p class="text-body-1 grey--text">
            {{ deleteAction === 'everyone'
              ? 'This will delete this message for all participants in this chat.'
              : 'This will delete this message only for you.'
            }}
          </p>
          <p class="text-body-2 grey--text mt-2">
            {{ deleteAction === 'everyone'
              ? 'This action cannot be undone.'
              : 'Other participants will still see this message.'
            }}
          </p>
        </v-card-text>
        <div class="px-4 pb-3">
          <v-btn block large depressed color="error" dark class="mb-3 rounded-pill" @click="confirmDelete"
            :loading="deletingMessage || deletingForMe">
            <v-icon left>mdi-delete</v-icon>
            {{ deleteAction === 'everyone' ? 'Delete for Everyone' : 'Delete for Me' }}
          </v-btn>
        </div>
        <v-card-actions class="px-0 pt-0 pb-4">
          <v-btn text block large class="rounded-lg text-capitalize" @click="cancelDeleteMessage">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Full Emoji Picker Dialog -->
    <v-dialog v-model="showFullEmojiPicker" max-width="500" persistent>
      <v-card class="emoji-picker-dialog rounded-xl">
        <v-card-title class="dialog-header">
          <span>Choose Reaction</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeFullEmojiPicker">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4 pb-2">
          <div class="emoji-picker-wrapper">
            <VEmojiPicker @select="selectEmojiFromPicker" :showSearch="true" :showCategories="true"
              :showSkinTones="true" />
          </div>

          <div v-if="currentSelectedMessage && hasAnyUserReaction(currentSelectedMessage)"
            class="current-reaction-status mt-3">
            <span class="status-text">
              Current reaction: <span class="current-emoji">{{ getUserReaction(currentSelectedMessage) }}</span>
              <span class="remove-link" @click="removeCurrentReaction">Remove</span>
            </span>
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="closeFullEmojiPicker" rounded>
            Done
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Message Dialog -->
    <edit-message v-model="editMessageDialog" :message="editingMessage" @save="handleSaveEditedMessage"
      @cancel="handleCancelEditMessage" />

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :timeout="4000" bottom :color="snackbarColor">
      <v-icon left>{{ snackbarIcon }}</v-icon>
      {{ snackMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import DOMPurify from "dompurify";
import { mapActions, mapGetters } from "vuex";
import EditableAvatar from "@/components/EditableAvatar.vue";
import EditMessage from "./EditMessages.vue";
import NoMessages from "./NoMessages.vue";
import socket from "@/socket";
import GroupInfoSidebar from "./GroupInfoSidebar.vue";


export default {
  name: "ChatMessages",
  components: {
    EditableAvatar, EditMessage, NoMessages,
    GroupInfoSidebar
  },

  data() {
    return {
      otherUser: null,
      newMessage: "",
      emojiMenu: false,
      attachmentMenu: false,
      selectedFiles: [],
      selectedImages: [],
      maxImages: 10,
      showSearch: false,
      searchQuery: "",
      showScrollButton: false,

      isTyping: false,
      typingTimeout: null,
      isUserTyping: false,
      userTypingTimeout: null,

      imagePreviewDialog: false,
      previewImages: [],
      currentPreviewImageIndex: 0,
      previewImageSrc: "",
      sendingMessage: false,
      pendingImages: new Map(),

      clearChatDialog: false,
      clearingChat: false,

      deleteMessageDialog: false,
      deletingMessage: false,
      deletingForMe: false,
      confirmDeleteMessage: null,
      deleteAction: 'me',
      clearForEveryone: false,

      searchTimeout: null,

      snackbar: false,
      snackMessage: "",
      snackbarIcon: "mdi-information",
      snackbarColor: "info",

      editMessageDialog: false,
      editingMessage: null,

      messageStatusMap: new Map(),
      messageMenu: {},
      reactionEmojis: ['👍', '❤️', '😂', '😮', '😢', '🙏'],
      reactionMenu: {},
      reactionLoading: {},

      // Full Emoji Picker
      showFullEmojiPicker: false,
      currentSelectedMessage: null,
      groupTypingUserName: "Someone",

      replyToMessageData: null,
      groupInfoDrawer: false,
    };
  },

  computed: {
    ...mapGetters({
      profile: "auth/getProfile",
      singleRoom: "chat/getSingleRoom",
      timezone: "usersetting/getTimeZone"
    }),

    canDeleteForEveryoneMessage() {
      return (message) => {
        if (!message) return false;
        // Only the sender can delete for everyone
        if (!this.isOwnMessage(message)) return false;

        // Check if message is within 68 hours (2 days and 12 hours)
        const messageTimeNY = this.convertTZ(message.createdAt || message.date, "America/New_York");
        const currentTimeNY = this.convertTZ(new Date(), "America/New_York");
        // 68 hours in milliseconds = 68 * 60 * 60 * 1000
        return (currentTimeNY.getTime() - messageTimeNY.getTime()) <= 68 * 60 * 60 * 1000;
      };
    },
    currentPreviewImage() {
      if (this.previewImages && this.previewImages.length > 0) {
        return this.previewImages[this.currentPreviewImageIndex] || this.previewImageSrc;
      }
      return this.previewImageSrc;
    },

    typingText() {
      if (!this.typingUsers) return '';
      const chatId = String(this.singleRoom?._id || '');
      const active = Object.values(this.typingUsers).filter(
        u => String(u.chatId) === chatId
      );
      if (!active.length) return '';
      if (active.length === 1) return `${active[0].name} is typing...`;
      if (active.length === 2) return `${active[0].name} and ${active[1].name} are typing...`;
      return `${active[0].name} and ${active.length - 1} others are typing...`;
    },
    isAnyoneTyping() {
      if (!this.typingUsers) return false;
      const chatId = String(this.singleRoom?._id || '');
      return Object.values(this.typingUsers).some(u => String(u.chatId) === chatId);
    },

    isGroupChat() {
      return this.singleRoom?.type === "group";
    },
    // Get filtered messages (excluding those deleted for current user)
    filteredRoomMessages() {
      if (!this.singleRoom?.messages) return [];

      const currentUserId = this.profile?._id;
      const currentUserType = this.profile?.type;

      return this.singleRoom.messages.filter(message => {
        // If message is deleted for everyone, exclude it
        if (message.isDeleted === true) return false;

        // Check if message is deleted for current user
        const isDeletedForUser = message.deletedFor?.some(
          d => d._id === currentUserId && d.userType === currentUserType
        );

        return !isDeletedForUser;
      });
    },

    chatHeaderName() {
      if (this.isGroupChat) {
        return this.singleRoom?.name || "Group Chat";
      }
      return this.getDisplayName();
    },

    chatHeaderAvatar() {
      if (this.isGroupChat) {
        return this.singleRoom?.groupImage || require("@/assets/default.jpg");
      }
      return this.getUserAvatar();
    },
    isGroupAdmin() {
      if (!this.isGroupChat || !this.singleRoom) return false;
      const currentUserId = this.profile?._id;
      const currentUserType = this.profile?.type;

      // Check if user is a group admin
      const isAdmin = this.singleRoom.groupAdmins?.some(
        admin => admin._id === currentUserId || admin.userType === currentUserType
      );

      // Check if user is the creator
      const isCreator = this.singleRoom.createdBy?._id === currentUserId;

      return isAdmin || isCreator;
    },

    activeChat() {
      return this.singleRoom; // Or whatever the current chat is
    },


    chatHeaderStatus() {
      if (this.isGroupChat) {
        const activeCount =
          this.singleRoom?.participants?.filter(p => p?.isActive !== false).length ||
          this.singleRoom?.participants?.length ||
          0;
        return activeCount + " participants";
      }
      return this.getUserStatus();
    },

    typingDisplayName() {
      if (this.isGroupChat) return this.groupTypingUserName || "Someone";
      return this.getDisplayName();
    },

    pinnedMessages() {
      const messages = this.filteredRoomMessages || [];
      return messages
        .filter(msg => msg.isPinned === true)
        .sort((a, b) => {
          const aTime = new Date(a.pinnedAt || a.createdAt || a.date || 0);
          const bTime = new Date(b.pinnedAt || b.createdAt || b.date || 0);
          return aTime - bTime;
        });
    },

    groupedMessages() {
      const messagesToGroup = this.searchQuery ? this.filteredMessages : this.filteredRoomMessages;
      if (!messagesToGroup || messagesToGroup.length === 0) return {};

      const groups = {};
      messagesToGroup.forEach((message) => {
        const date = new Date(message.createdAt || message.date || message.timestamp || Date.now()).toDateString();
        if (!groups[date]) groups[date] = [];
        groups[date].push(message);
      });
      return groups;
    },

    editableMessageIds() {
      if (!this.singleRoom?.messages) return new Set();
      const currentTimeNY = this.convertTZ(new Date(), "America/New_York");
      const fifteenMinutes = 15 * 60 * 1000;
      const editableIds = new Set();

      this.singleRoom.messages.forEach((message) => {
        if (message && (message.createdAt || message.date)) {
          const messageTimeNY = this.convertTZ(message.createdAt || message.date, "America/New_York");
          const timeDifference = currentTimeNY.getTime() - messageTimeNY.getTime();
          if (timeDifference <= fifteenMinutes && this.isOwnMessage(message)) {
            editableIds.add(message._id);
          }
        }
      });
      return editableIds;
    },
    // Check if there are messages
    hasMessages() {
      if (!this.singleRoom) return false;
      if (!this.singleRoom.messages) return false;
      if (!Array.isArray(this.singleRoom.messages)) return false;

      // Filter out system messages, deleted messages, and messages deleted for current user
      const visibleMessages = this.singleRoom.messages.filter(msg => {
        // Skip if message is deleted
        if (msg.isDeleted === true) return false;

        // Skip system messages
        if (msg.messageType === 'system') return false;
        if (msg.systemMeta && msg.systemMeta.action) return false;

        // Skip messages deleted for current user
        if (msg.deletedFor && this.profile) {
          const isDeletedForUser = msg.deletedFor.some(
            d => d._id === this.profile._id && d.userType === this.profile.type
          );
          if (isDeletedForUser) return false;
        }

        return true;
      });

      return visibleMessages.length > 0;
    },

    filteredMessages() {
      if (!this.searchQuery) return this.filteredRoomMessages;
      return this.filteredRoomMessages.filter((message) => {
        const messageText = (message.text || message.message || "").toLowerCase();
        return messageText.includes(this.searchQuery.toLowerCase());
      });
    },

    canDeleteForEveryone() {
      if (!this.confirmDeleteMessage) return false;
      // Only the sender can delete for everyone
      if (!this.isOwnMessage(this.confirmDeleteMessage)) return false;

      const messageTimeNY = this.convertTZ(this.confirmDeleteMessage.createdAt || this.confirmDeleteMessage.date, "America/New_York");
      const currentTimeNY = this.convertTZ(new Date(), "America/New_York");
      // 68 hours in milliseconds = 68 * 60 * 60 * 1000
      return (currentTimeNY.getTime() - messageTimeNY.getTime()) <= 68 * 60 * 60 * 1000;
    },
  },

  watch: {
    singleRoom: {
      immediate: true,
      handler(newVal, oldVal) {
        // Clear input when switching chats
        if (newVal && oldVal && newVal._id !== oldVal._id) {
          this.clearInputState();
        }

        if (newVal) {
          if (!newVal.messages) this.$set(newVal, 'messages', []);
          this.updateOtherUser();

          // Join room via socket
          if (newVal._id) {
            // Leave old room if exists
            if (oldVal && oldVal._id && oldVal._id !== newVal._id) {
              socket.leaveChatRoom(oldVal._id);
            }

            // Join new room
            socket.joinChatRoom(newVal._id);

            // Mark messages as read after a slight delay
            this.$nextTick(() => {
              setTimeout(() => {
                this.markMessagesAsRead();
              }, 300);
            });
          }

          this.$nextTick(() => this.scrollToBottom());
        }
      },
      deep: true
    },

    '$route.query.chatId': {
      immediate: true,
      handler(newChatId, oldChatId) {
        // Clear input when navigating to a different chat
        if (newChatId && oldChatId && newChatId !== oldChatId) {
          this.clearInputState();
        }

        if (!newChatId) return;
        if (this.singleRoom && this.singleRoom._id === newChatId) return;

        this.$store.dispatch('chat/getChatById', newChatId)
          .then(chat => {
            if (chat) {
              if (!chat.messages) chat.messages = [];
              this.$store.commit('chat/setSingleRoom', chat);
            }
          })
          .catch(() => this.$store.commit('chat/setSingleRoom', null));
      }
    },
    // Watch for route changes to clear input
    '$route': {
      handler(to, from) {
        // Check if chatId changed
        const toChatId = to.query.chatId;
        const fromChatId = from.query.chatId;

        if (toChatId && fromChatId && toChatId !== fromChatId) {
          this.clearInputState();
        }

        // Also clear if navigating away from chat
        if (!toChatId && fromChatId) {
          this.clearInputState();
        }
      },
      immediate: false
    }
  },


  methods: {
    ...mapActions({
      clearChat: "chat/clearChat",
      deleteSingleMessage: "chat/deleteMessage", // Delete for me
      deleteMessageForEveryone: "chat/deleteMessageForEveryone", // Delete for everyone
      editSingleMessage: "chat/editMessage",
      addReaction: "chat/addReaction",
      removeReaction: "chat/removeReaction",
      pinMessage: "chat/pinMessage"
    }),

    updateOtherUser() {
      if (!this.singleRoom || !this.profile) return;

      // For group chats, set otherUser to null
      if (this.isGroupChat) {
        this.otherUser = null;
        return;
      }

      // If a selectedUser was provided (from the new-chat popup), use it directly
      if (this.singleRoom.selectedUser) {
        this.otherUser = this.normalizeUserData(this.singleRoom.selectedUser);
        return;
      }

      let other = null;

      // Check participants
      if (this.singleRoom.participants && Array.isArray(this.singleRoom.participants)) {
        // Find participant that is not the current user
        other = this.singleRoom.participants.find(p => {
          const pId = p._id || p.userID || p.id || p.userId;
          return String(pId) !== String(this.profile._id);
        });

        if (other) {
          this.otherUser = this.normalizeUserData(other);
          return;
        }
      }

      // If no opposite found, try to get from createdBy
      if (this.singleRoom.createdBy) {
        const createdBy = this.singleRoom.createdBy;
        const createdById = createdBy._id || createdBy.userID || createdBy.id;
        if (String(createdById) !== String(this.profile._id)) {
          this.otherUser = this.normalizeUserData(createdBy);
          return;
        }
      }

      // If still no other user, use first participant
      if (this.singleRoom.participants && this.singleRoom.participants.length > 0) {
        this.otherUser = this.normalizeUserData(this.singleRoom.participants[0]);
        return;
      }

      this.otherUser = null;
    },
    clearInputState() {
      this.newMessage = "";
      this.selectedFiles = [];
      this.selectedImages = [];
      this.replyToMessageData = null;
      this.searchQuery = "";
      this.showSearch = false;

      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
        this.typingTimeout = null;
      }
      this.isTyping = false;
      this.stopTyping();

      this.messageMenu = {};
      this.reactionMenu = {};
    },
    // Add this method in the methods section
    getMessageFiles(message) {
      if (!message) return [];

      // Check for attachments that are not images
      if (message.attachments && Array.isArray(message.attachments)) {
        return message.attachments.filter(att => {
          // Filter out image attachments (they're handled separately)
          const isImage = att.type === 'image' ||
            (att.mimetype && att.mimetype.startsWith('image/')) ||
            /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(att.url || '');
          return !isImage && att.url;
        });
      }

      return [];
    },

    // Get file icon based on type
    getFileIcon(file) {
      if (!file) return 'mdi-file';

      const type = file.type || file.mimetype || '';
      const url = file.url || '';

      if (type === 'pdf' || url.endsWith('.pdf')) return 'mdi-file-pdf';
      if (type === 'document' || type === 'doc' || url.endsWith('.doc') || url.endsWith('.docx')) return 'mdi-file-word';
      if (type === 'spreadsheet' || type === 'xls' || url.endsWith('.xls') || url.endsWith('.xlsx')) return 'mdi-file-excel';
      if (type === 'presentation' || type === 'ppt' || url.endsWith('.ppt') || url.endsWith('.pptx')) return 'mdi-file-powerpoint';
      if (type === 'video' || url.match(/\.(mp4|mov|avi|mkv|webm)$/i)) return 'mdi-file-video';
      if (type === 'audio' || url.match(/\.(mp3|wav|ogg|flac|aac)$/i)) return 'mdi-file-music';
      if (type === 'zip' || url.match(/\.(zip|rar|7z|tar|gz)$/i)) return 'mdi-file-zip';
      if (type === 'text' || url.endsWith('.txt')) return 'mdi-file-document';

      return 'mdi-file';
    },

    // Get file color based on type
    getFileColor(file) {
      if (!file) return 'grey';

      const type = file.type || file.mimetype || '';
      const url = file.url || '';

      if (type === 'pdf' || url.endsWith('.pdf')) return 'red';
      if (type === 'document' || type === 'doc' || url.endsWith('.doc') || url.endsWith('.docx')) return 'blue';
      if (type === 'spreadsheet' || type === 'xls' || url.endsWith('.xls') || url.endsWith('.xlsx')) return 'green';
      if (type === 'presentation' || type === 'ppt' || url.endsWith('.ppt') || url.endsWith('.pptx')) return 'orange';
      if (type === 'video' || url.match(/\.(mp4|mov|avi|mkv|webm)$/i)) return 'deep-purple';
      if (type === 'audio' || url.match(/\.(mp3|wav|ogg|flac|aac)$/i)) return 'pink';
      if (type === 'zip' || url.match(/\.(zip|rar|7z|tar|gz)$/i)) return 'brown';

      return 'grey';
    },

    // Get file name from attachment
    getFileName(file) {
      if (!file) return 'File';

      // If filename is provided
      if (file.filename) return file.filename;
      if (file.name) return file.name;
      if (file.originalname) return file.originalname;

      // Extract from URL
      if (file.url) {
        const urlParts = file.url.split('/');
        const lastPart = urlParts[urlParts.length - 1];
        // Remove query parameters
        const fileName = lastPart.split('?')[0];
        if (fileName) return fileName;
      }

      // Default
      return 'File';
    },

    // Get file size formatted
    getFileSize(file) {
      if (!file || !file.size) return '';

      const bytes = file.size;
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
      return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
    },

    // Open file in new tab
    openFile(file) {
      if (file && file.url) {
        window.open(file.url, '_blank');
      }
    },

    // Download file
    downloadFile(file) {
      if (file && file.url) {
        const link = document.createElement('a');
        link.href = file.url;
        link.download = this.getFileName(file);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    setupSocketListeners() {
      this.cleanupSocketListeners();

      // Create bound handlers
      this._newMessageHandler = (data) => {
        this.handleNewMessage(data);
      };
      // NEW: Group updated handler
      this._groupUpdatedHandler = (data) => {
        this.handleGroupUpdated(data);
      };

      this._messagesReadHandler = (data) => this.handleMessagesRead(data);
      this._messageDeliveredHandler = (data) => this.handleMessageDelivered(data);
      this._messageDeletedHandler = (data) => this.handleMessageDeleted(data);
      this._messageDeletedForEveryoneHandler = (data) => this.handleMessageDeletedForEveryone(data);
      this._reactionUpdatedHandler = (data) => this.handleReactionUpdated(data);
      this._messagePinnedHandler = (data) => this.handleMessagePinned(data);
      this._typingStartHandler = (data) => this.handleTypingStart(data);
      this._typingStopHandler = (data) => this.handleTypingStop(data);
      this._roomJoinedHandler = (data) => { console.log("room-joined:", data); };

      // Register listeners
      socket.on("new-message", this._newMessageHandler);
      socket.on("messages-read", this._messagesReadHandler);
      socket.on("message-delivered", this._messageDeliveredHandler);
      socket.on("message-deleted", this._messageDeletedHandler);
      socket.on("message-deleted-for-everyone", this._messageDeletedForEveryoneHandler);
      socket.on("group-updated", this._groupUpdatedHandler);
      socket.on("reaction-updated", this._reactionUpdatedHandler);
      socket.on("message-pinned", this._messagePinnedHandler);
      socket.on("typing-start", this._typingStartHandler);
      socket.on("typing-stop", this._typingStopHandler);
      socket.on("room-joined", this._roomJoinedHandler);

      // Add admin_added listener
      this._adminAddedHandler = (data) => {
        this.handleAdminAdded(data);
      };
      socket.on('admin_added', this._adminAddedHandler);

      // Add admin_removed listener
      this._adminRemovedHandler = (data) => {
        this.handleAdminRemoved(data);
      };
      socket.on('admin_removed', this._adminRemovedHandler);

      // Listen for member_removed events
      this._memberRemovedHandler = (data) => {
        this.handleSocketMemberRemoved(data);
      };
      socket.on('member_removed', this._memberRemovedHandler);

      // Listen for member_added events
      this._memberAddedHandler = (data) => {
        this.handleSocketMemberAdded(data);
      };
      socket.on('member_added', this._memberAddedHandler);

      // Listen for user_left_group events
      this._userLeftGroupHandler = (data) => {
        this.handleUserLeftGroup(data);
      };
      socket.on('user_left_group', this._userLeftGroupHandler);

      // Listen for you_left_group events
      this._youLeftGroupHandler = (data) => {
        this.handleYouLeftGroup(data);
      };
      socket.on('you_left_group', this._youLeftGroupHandler);
    },

    // In ChatMessages.vue

    handleGroupUpdated(data) {
      if (!data || data.chatId !== String(this.singleRoom?._id || "")) return;

      console.log('Group updated via socket:', data);

      // Update the chat header with new values
      if (data.updates && this.singleRoom) {
        if (data.updates.name) {
          this.singleRoom.name = data.updates.name;
          // Also update in the chat list
          this.$store.commit('chat/UPDATE_CHAT', {
            _id: this.singleRoom._id,
            name: data.updates.name
          });
        }
        if (data.updates.groupImage) {
          this.singleRoom.groupImage = data.updates.groupImage;
          this.$store.commit('chat/UPDATE_CHAT', {
            _id: this.singleRoom._id,
            groupImage: data.updates.groupImage
          });
        }
        if (data.updates.description !== undefined) {
          this.singleRoom.description = data.updates.description;
        }
      }

      // Add system message to the chat
      if (data.systemMessage && this.singleRoom) {
        // Ensure messages array exists
        if (!this.singleRoom.messages) {
          this.singleRoom.messages = [];
        }

        // Check if system message already exists (prevent duplicates)
        const exists = this.singleRoom.messages.some(
          m => m._id && m._id === data.systemMessage._id
        );

        if (!exists) {
          // Add the system message
          const newMessage = {
            ...data.systemMessage,
            messageType: 'system',
            countsTowardUnread: false,
            _id: data.systemMessage._id || `sys_${Date.now()}`
          };

          this.singleRoom.messages.push(newMessage);

          // Also update the chat in the store
          this.$store.commit('chat/UPDATE_CHAT', {
            _id: this.singleRoom._id,
            messages: this.singleRoom.messages
          });

          // Force UI update
          this.$forceUpdate();

          // Scroll to bottom if near bottom
          this.$nextTick(() => {
            const container = this.$refs.messagesContainer;
            if (container) {
              const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
              if (nearBottom) {
                container.scrollTop = container.scrollHeight;
              }
            }
          });
        }
      }

      // Update the chat in the store
      if (this.singleRoom) {
        this.$store.commit('chat/UPDATE_CHAT', this.singleRoom);
      }
    },
    handleUserLeftGroup(data) {
      // Update local state when another user leaves the group
      if (!data || data.chatId !== this.singleRoom?._id) return;

      // Update participants list - remove the user who left
      if (this.singleRoom.participants && Array.isArray(this.singleRoom.participants)) {
        const participantIndex = this.singleRoom.participants.findIndex(
          p => (p._id || p.userID) === data.userId
        );
        if (participantIndex !== -1) {
          // Mark as inactive or remove
          this.singleRoom.participants[participantIndex].isActive = false;
          this.singleRoom.participants[participantIndex].leftAt = data.leftAt;
        }
      }

      // Add system message
      if (data.systemMessage && this.singleRoom.messages) {
        this.singleRoom.messages.push({
          ...data.systemMessage,
          messageType: 'system',
          systemMeta: {
            action: 'user_left',
            summary: data.systemMessage.text,
            userId: data.userId,
            userName: data.userName,
          },
          countsTowardUnread: false,
          createdAt: data.leftAt || new Date()
        });
        this.$forceUpdate();
      }

      // Update group admins if admin was transferred
      if (data.adminAssigned && data.newAdmin) {
        // Update group admins in local state
        if (this.singleRoom.groupAdmins) {
          // Remove the old admin
          this.singleRoom.groupAdmins = this.singleRoom.groupAdmins.filter(
            admin => admin._id !== data.userId
          );
          // Add new admin if not already present
          const exists = this.singleRoom.groupAdmins.some(
            admin => admin._id === data.newAdmin._id
          );
          if (!exists) {
            this.singleRoom.groupAdmins.push(data.newAdmin);
          }
        }
      }

      // Update creator if transferred
      if (data.newCreator && this.singleRoom.createdBy) {
        this.singleRoom.createdBy = data.newCreator;
      }

      // Update participant count
      this.$forceUpdate();
    },

    handleYouLeftGroup(data) {
      // Handle when the current user leaves the group
      if (!data || data.chatId !== this.singleRoom?._id) return;

      // Show a message
      this.showSnackbar('You have left the group', 'info', 'mdi-information');

      // Clear the chat
      this.$store.commit('chat/setSingleRoom', null);

      // Navigate away
      this.$router.push({ name: 'chat-details', query: {} });
    },

    handleSocketMemberRemoved(data) {
      if (!data || data.chatId !== this.singleRoom?._id) return;

      // Create system message with proper text
      let systemMessage = {
        _id: data.systemMessage?._id || `sys_${Date.now()}`,
        messageType: 'system',
        text: data.systemMessage?.text || `${data.removedBy?.name || 'Someone'} removed ${data.removedMember?.name || 'a member'} from the group`,
        systemMeta: {
          action: 'member_removed',
          summary: data.systemMessage?.text || `${data.removedBy?.name || 'Someone'} removed ${data.removedMember?.name || 'a member'} from the group`,
          removedUser: data.removedMember,
          removedBy: data.removedBy,
        },
        countsTowardUnread: false,
        createdAt: data.systemMessage?.createdAt || new Date(),
        sender: {
          _id: data.removedBy?._id || '',
          name: data.removedBy?.name || 'Someone',
        }
      };

      // Add to messages
      if (this.singleRoom.messages) {
        this.singleRoom.messages.push(systemMessage);
        this.$forceUpdate();
      }
    },

    handleSocketMemberAdded(data) {
      if (!data || data.chatId !== this.singleRoom?._id) return;

      // Create system message with proper text
      let systemMessage = {
        _id: data.systemMessage?._id || `sys_${Date.now()}`,
        messageType: 'system',
        text: data.systemMessage?.text || `${data.addedBy?.name || 'Someone'} added ${data.newMember?.name || 'a member'} to the group`,
        systemMeta: {
          action: 'member_added',
          summary: data.systemMessage?.text || `${data.addedBy?.name || 'Someone'} added ${data.newMember?.name || 'a member'} to the group`,
          addedUser: data.newMember,
          addedBy: data.addedBy,
        },
        countsTowardUnread: false,
        createdAt: data.systemMessage?.createdAt || new Date(),
        sender: {
          _id: data.addedBy?._id || '',
          name: data.addedBy?.name || 'Someone',
        }
      };

      // Add to messages
      if (this.singleRoom.messages) {
        this.singleRoom.messages.push(systemMessage);
        this.$forceUpdate();
      }
    },

    handleAdminAdded(data) {
      // Update local state if this is the current chat
      if (data.chatId === this.singleRoom?._id) {
        // Add system message
        if (data.systemMessage && this.singleRoom.messages) {
          this.singleRoom.messages.push({
            ...data.systemMessage,
            messageType: 'system',
            systemMeta: {
              action: 'admin_added',
              summary: data.systemMessage.text
            },
            countsTowardUnread: false
          });
          this.$forceUpdate();
        }

        // Update group admins in sidebar if needed
        if (this.$refs.groupInfoSidebar) {
          this.$refs.groupInfoSidebar.updateGroupData(data);
        }
      }
    },

    cleanupSocketListeners() {
      const pairs = [
        ['new-message', this._newMessageHandler],
        ['messages-read', this._messagesReadHandler],
        ['message-delivered', this._messageDeliveredHandler],
        ['message-deleted', this._messageDeletedHandler],
        ['message-deleted-for-everyone', this._messageDeletedForEveryoneHandler],
        ['reaction-updated', this._reactionUpdatedHandler],
        ['message-pinned', this._messagePinnedHandler],
        ['typing-start', this._typingStartHandler],
        ['typing-stop', this._typingStopHandler],
        ['room-joined', this._roomJoinedHandler],
        ['group-updated', this._groupUpdatedHandler],

      ];
      pairs.forEach(([event, ref]) => {
        if (ref) socket.off(event, ref);
      });
      this._newMessageHandler = null;
      this._messagesReadHandler = null;
      this._messageDeliveredHandler = null;
      this._messageDeletedHandler = null;
      this._messageDeletedForEveryoneHandler = null;
      this._reactionUpdatedHandler = null;
      this._messagePinnedHandler = null;
      this._typingStartHandler = null;
      this._typingStopHandler = null;
      this._roomJoinedHandler = null;
      this._groupUpdatedHandler = null;
      if (this._adminAddedHandler) {
        socket.off('admin_added', this._adminAddedHandler);
        this._adminAddedHandler = null;
      }
      if (this._adminRemovedHandler) {
        socket.off('admin_removed', this._adminRemovedHandler);
        this._adminRemovedHandler = null;
      }
      if (this._memberRemovedHandler) {
        socket.off('member_removed', this._memberRemovedHandler);
        this._memberRemovedHandler = null;
      }
      if (this._memberAddedHandler) {
        socket.off('member_added', this._memberAddedHandler);
        this._memberAddedHandler = null;
      }
      if (this._userLeftGroupHandler) {
        socket.off('user_left_group', this._userLeftGroupHandler);
        this._userLeftGroupHandler = null;
      }
      if (this._youLeftGroupHandler) {
        socket.off('you_left_group', this._youLeftGroupHandler);
        this._youLeftGroupHandler = null;
      }
    },
    handleAdminRemoved(data) {
      // Update local state if this is the current chat
      if (data.chatId === this.singleRoom?._id) {
        // Add system message
        if (data.systemMessage && this.singleRoom.messages) {
          this.singleRoom.messages.push({
            ...data.systemMessage,
            messageType: 'system',
            systemMeta: {
              action: 'admin_removed',
              summary: data.systemMessage.text
            },
            countsTowardUnread: false
          });
          this.$forceUpdate();
        }

        // Update group admins in sidebar if needed
        if (this.$refs.groupInfoSidebar) {
          this.$refs.groupInfoSidebar.handleAdminRemoved(data);
        }
      }
    },

    // In ChatMessages.vue - methods

    handleNewMessage(data) {
      const chatId = String(data?.chatId || "");
      const message = data?.message;

      if (!chatId || !message) return;

      // For system messages, ensure text is set
      if (message.messageType === 'system') {
        // If text is empty but we have systemMeta, generate text
        if (!message.text || message.text.trim() === '') {
          if (message.systemMeta?.summary) {
            message.text = message.systemMeta.summary;
          } else if (message.systemMeta?.action) {
            // Generate text from action
            const action = message.systemMeta.action;
            const actorName = message.sender?.name || 'Someone';
            const targetName = message.systemMeta.removedUser?.name ||
              message.systemMeta.addedUser?.name ||
              '';
            if (action === 'member_added') {
              message.text = `${actorName} added ${targetName || 'a member'} to the group`;
            } else if (action === 'member_removed') {
              message.text = `${actorName} removed ${targetName || 'a member'} from the group`;
            } else if (action === 'admin_added') {
              message.text = `${actorName} made ${targetName || 'a member'} a group admin`;
            } else if (action === 'admin_removed') {
              message.text = `${actorName} removed ${targetName || 'a member'} as group admin`;
            }
          }
        }
      }

      // Update the chat list sidebar last message regardless
      this.$store.commit("chat/UPDATE_CHAT", {
        _id: chatId,
        lastMessage: message,
        lastActivityAt: message.createdAt || new Date().toISOString()
      });

      // Only update messages array if this is the active chat
      if (chatId !== String(this.singleRoom?._id || "")) return;
      if (!Array.isArray(this.singleRoom.messages)) return;

      const incomingId = String(message._id || "");
      const incomingClientId = String(message.clientMessageId || "");

      // Check if this exact message already exists (by _id)
      if (incomingId) {
        const exists = this.singleRoom.messages.some(
          m => m && String(m._id) === incomingId
        );
        if (exists) return;
      }

      // Replace temp message by clientMessageId
      if (incomingClientId) {
        const tempIdx = this.singleRoom.messages.findIndex(
          m => m && (
            String(m.clientMessageId) === incomingClientId ||
            String(m._id) === incomingClientId
          )
        );
        if (tempIdx !== -1) {
          this.$set(this.singleRoom.messages, tempIdx, { ...message, sending: false });
          this.$forceUpdate();
          return;
        }
      }

      // Prevent duplicates by checking recent messages (within last 3 seconds)
      const now = Date.now();
      const recentMessages = this.singleRoom.messages.filter(m => {
        if (!m) return false;
        const msgTime = new Date(m.createdAt || m.timestamp || 0).getTime();
        return (now - msgTime) < 3000;
      });

      const isDuplicate = recentMessages.some(m => {
        if (!m) return false;
        const senderId = String(m?.sender?._id || m?.senderID || m?.fromUser || "");
        const newSenderId = String(message?.sender?._id || message?.senderID || message?.fromUser || "");
        if (senderId !== newSenderId) return false;

        const mText = (m.text || m.message || "").trim();
        const newText = (message.text || message.message || "").trim();
        if (mText && newText && mText === newText) return true;

        if (m.clientMessageId && message.clientMessageId &&
          m.clientMessageId === message.clientMessageId) return true;

        return false;
      });

      if (isDuplicate) {
        console.warn('Duplicate message detected, skipping:', message._id);
        return;
      }

      // New message - add it
      this.singleRoom.messages.push({
        ...message,
        isTemp: false,
        sending: false
      });
      this.$forceUpdate();

      // Auto-scroll if near bottom
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (!container) return;
        const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
        if (nearBottom) {
          container.scrollTop = container.scrollHeight;
          this.markMessagesAsRead();
        }
      });
    },

    handleMessageRead(data) {
      const { messageId } = data;
      if (!messageId) return;

      // Update message status
      if (this.singleRoom && this.singleRoom.messages) {
        const message = this.singleRoom.messages.find(m => m._id === messageId);
        if (message) {
          message.read = true;
          message.status = 'read';
          this.$forceUpdate();
        }
      }
    },

    // In ChatMessages.vue - find the handleMessagesRead method and update it

    handleMessagesRead(data) {
      const chatId = String(data?.chatId || "");
      const readerId = String(data?.userId || "");
      const lastReadSequence = Number(data?.lastReadSequence || 0);

      if (!chatId || !readerId || lastReadSequence <= 0) return;
      if (chatId !== String(this.singleRoom?._id || "")) return;
      if (!Array.isArray(this.singleRoom?.messages)) return;

      const myId = String(this.profile?._id || "");

      this.singleRoom.messages.forEach((message) => {
        if (!message) return;

        const senderId = String(
          message?.sender?._id || message?.senderID || message?.fromUser || ""
        );

        // Only update read indicator on messages I sent
        if (!myId || senderId !== myId) return;

        const seq = Number(message?.sequenceNumber || 0);
        if (!seq || seq > lastReadSequence) return;

        const seenBy = Array.isArray(message.seenBy) ? message.seenBy : [];
        if (!seenBy.some((s) => String(s?._id) === readerId)) {
          seenBy.push({
            _id: readerId,
            userType: data?.userType || "User",
            seenAt: new Date()
          });
          this.$set(message, "seenBy", seenBy);
        }

        this.$set(message, "status", "read");
      });

      this.$forceUpdate();
    },

    async markMessagesAsRead() {
      if (!this.singleRoom?._id || !this.profile?._id) return;
      if (!Array.isArray(this.singleRoom.messages) || !this.singleRoom.messages.length) return;

      const myId = String(this.profile._id);
      const myType = this.profile.userType || this.profile.type || "User";

      // Find messages sent by others that I haven't seen yet
      const unread = this.singleRoom.messages.filter((m) => {
        if (!m || m.isDeleted) return false;
        if (m.countsTowardUnread === false) return false;
        const senderId = String(m?.sender?._id || m?.senderID || m?.fromUser || "");
        if (senderId === myId) return false;
        const seenBy = Array.isArray(m.seenBy) ? m.seenBy : [];
        return !seenBy.some((s) => String(s?._id) === myId);
      });

      if (!unread.length) return;

      // Get the highest sequenceNumber among unread messages
      const lastReadSequence = unread.reduce((max, m) => {
        const seq = Number(m.sequenceNumber || 0);
        return seq > max ? seq : max;
      }, 0);

      if (!lastReadSequence) return;

      const payload = {
        chatId: String(this.singleRoom._id),
        lastReadSequence,
        userId: myId,
        userType: myType
      };

      try {
        await socket.sendMessagesRead(payload);
      } catch (err) {
        console.warn("markMessagesAsRead socket error:", err?.message);
      }

      // Optimistic local update
      unread.forEach((m) => {
        const seenBy = Array.isArray(m.seenBy) ? m.seenBy : [];
        if (!seenBy.some((s) => String(s?._id) === myId)) {
          seenBy.push({ _id: myId, userType: myType, seenAt: new Date() });
          this.$set(m, "seenBy", seenBy);
        }
        this.$set(m, "status", "read");
      });

      this.$forceUpdate();
    },

    handleMessageDeleted(data) {
      const { messageId } = data;
      if (!messageId) return;

      // Remove message from view
      if (this.singleRoom && this.singleRoom.messages) {
        this.singleRoom.messages = this.singleRoom.messages.filter(
          m => m._id !== messageId
        );
        this.$forceUpdate();
      }
    },

    handleMessageDeletedForEveryone(data) {
      const { messageId } = data;
      if (!messageId) return;

      // Mark message as deleted for everyone (isDeleted stays false so it
      // remains visible as a system message, matching backend behavior)
      if (this.singleRoom && this.singleRoom.messages) {
        const message = this.singleRoom.messages.find(m => m._id === messageId);
        if (message) {
          this.$set(message, 'isDeleted', false);
          this.$set(message, 'text', 'This message was deleted');
          this.$set(message, 'attachments', []);
          this.$set(message, 'messageType', 'system');
          this.$set(message, 'systemMeta', {
            action: 'message_deleted',
            summary: 'Message was deleted'
          });
          this.$forceUpdate();
        }
      }
    },

    handleReactionUpdated(data) {
      const { messageId, emoji, user, action } = data;
      if (!messageId) return;

      if (this.singleRoom && this.singleRoom.messages) {
        const message = this.singleRoom.messages.find(m => m._id === messageId);
        if (message) {
          if (!message.reactions) {
            message.reactions = [];
          }

          if (action === 'add') {
            // Add reaction
            const existing = message.reactions.find(r => r.emoji === emoji);
            if (existing) {
              if (!existing.users.find(u => u._id === user._id)) {
                existing.users.push(user);
              }
            } else {
              message.reactions.push({ emoji, users: [user] });
            }
          } else if (action === 'remove') {
            // Remove reaction
            const reactionIndex = message.reactions.findIndex(r => r.emoji === emoji);
            if (reactionIndex !== -1) {
              const reaction = message.reactions[reactionIndex];
              reaction.users = reaction.users.filter(u => u._id !== user._id);
              if (reaction.users.length === 0) {
                message.reactions.splice(reactionIndex, 1);
              }
            }
          }
          this.$forceUpdate();
        }
      }
    },

    handleMessagePinned(data) {
      const { messageId, pinned } = data;
      if (!messageId) return;

      if (this.singleRoom && this.singleRoom.messages) {
        const message = this.singleRoom.messages.find(m => m._id === messageId);
        if (message) {
          message.isPinned = pinned;
          if (pinned) {
            message.pinnedAt = new Date();
          } else {
            delete message.pinnedAt;
          }
          this.$forceUpdate();
        }
      }
    },


    handleClickOutside(event) {
      if (this.showSearch) {
        const searchBar = this.$el.querySelector('.search-bar');
        const searchButton = this.$el.querySelector('.header-actions .v-btn:first-child');
        if (searchBar && !searchBar.contains(event.target) &&
          searchButton && !searchButton.contains(event.target)) {
          this.closeSearch();
        }
      }
    },
    clearSearch() {
      this.searchQuery = "";
      this.searchMessages();
      // Focus the input after clearing
      this.$nextTick(() => {
        const searchInput = this.$refs.searchInput;
        if (searchInput) {
          searchInput.focus();
        }
      });
    },
    // Get pinned message sender name
    getPinnedMessageSender(message) {
      if (!message) return 'Unknown';
      if (message.sender?.name) return message.sender.name;
      if (message.sender?.first_name) {
        return `${message.sender.first_name} ${message.sender.last_name || ''}`.trim();
      }
      if (message.fromUser) {
        // Try to find user in participants
        const participant = this.singleRoom?.participants?.find(p =>
          p._id === message.fromUser || p.userID === message.fromUser
        );
        if (participant) return participant.name || 'User';
      }
      if (this.isOwnMessage(message)) return 'You';
      return 'User';
    },


    // Get pinned message preview text
    getPinnedMessagePreview(message) {
      if (!message) return '';
      if (message.text) return message.text.length > 60 ? message.text.slice(0, 60) + '...' : message.text;
      if (message.message) return message.message.length > 60 ? message.message.slice(0, 60) + '...' : message.message;

      // Check for attachments
      if (message.attachments && message.attachments.length > 0) {
        const imageAttachments = message.attachments.filter(att => att.type && att.type.startsWith('image/'));
        if (imageAttachments.length > 0) {
          return `📷 ${imageAttachments.length} photo(s)`;
        }
        return `📎 ${message.attachments.length} attachment(s)`;
      }

      if (message.messageType === 'image') return '📷 Photo';
      if (message.messageType === 'video') return '🎬 Video';
      if (message.messageType === 'document') return '📄 Document';
      if (message.messageType === 'file') return '📎 File';

      return 'Message';
    },

    // Scroll to a pinned message
    scrollToPinnedMessage(message) {
      if (!message) return;
      const messageId = message._id || message.clientMessageId;
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        const messageElement = container?.querySelector(`[data-message-id="${messageId}"]`);
        if (messageElement) {
          // Highlight the message briefly
          messageElement.classList.add('highlight-pinned');
          messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Remove highlight after animation
          setTimeout(() => {
            messageElement.classList.remove('highlight-pinned');
          }, 3000);
        }
      });
    },


    normalizeUserData(user) {
      if (!user) return null;

      // If user is a string ID
      if (typeof user === 'string') {
        return {
          _id: user,
          userID: user,
          name: 'User',
          userType: 'User',
          isOnline: false,
          image: null
        };
      }

      // Get user ID from various possible fields
      const userId = user._id || user.userID || user.id || user.userId;
      if (!userId) return null;

      // Get display name - try various fields
      let displayName = '';
      if (user.name && user.name.trim()) {
        displayName = user.name.trim();
      } else if (user.first_name && user.last_name) {
        displayName = `${user.first_name} ${user.last_name}`.trim();
      } else if (user.first_name) {
        displayName = user.first_name;
      } else if (user.last_name) {
        displayName = user.last_name;
      } else if (user.displayName && user.displayName.trim()) {
        displayName = user.displayName.trim();
      } else if (user.email && user.email.trim()) {
        displayName = user.email.split('@')[0];
      } else {
        displayName = 'User';
      }

      // Get user type
      const userType = user.userType || user.type || user.role || 'User';

      // Get image
      const image = user.image || user.thumbnail || user.midThumbnail || user.avatar || user.profileImage || null;

      return {
        ...user,
        _id: userId,
        userID: userId,
        name: displayName,
        image: image,
        isOnline: user.isOnline || false,
        lastSeen: user.lastSeen || user.lastActive || null,
        userType: userType,
        email: user.email || null
      };
    },

    // Toggle pin/unpin message
    async togglePinMessage(message) {
      if (!message) {
        this.showSnackbar("Message not found", "error", "mdi-alert");
        return;
      }

      try {
        const result = await this.pinMessage({
          messageId: message._id,
          chatId: this.singleRoom._id
        });

        if (result && result.success) {
          // Update local state
          this.$set(message, 'isPinned', result.pinned);
          if (result.pinned) {
            this.$set(message, 'pinnedAt', new Date());
            this.showSnackbar("Message pinned", "success", "mdi-pin");
          } else {
            this.$delete(message, 'pinnedAt');
            this.showSnackbar("Message unpinned", "success", "mdi-pin-off");
          }

          // Close any open menus
          this.reactionMenu = {};
          this.contextMenu = { show: false };

          // Force update
          this.$forceUpdate();

          // If pinned, scroll to the message
          if (result.pinned) {
            this.$nextTick(() => {
              this.scrollToPinnedMessage(message);
            });
          }
        } else {
          this.showSnackbar(result?.message || "Failed to pin message", "error", "mdi-alert");
        }
      } catch (error) {
        console.error("Error toggling pin:", error);
        this.showSnackbar(error.message || "Failed to pin message", "error", "mdi-alert");
      }
    },

    getUserAvatar() {
      if (!this.otherUser) return require("@/assets/default.jpg");
      return this.otherUser.image ||
        this.otherUser.midThumbnail ||
        this.otherUser.thumbnail ||
        this.otherUser.avatar ||
        require("@/assets/default.jpg");
    },

    // Add this method
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    getDisplayName() {
      if (!this.otherUser) return "User";
      return this.otherUser.name || "User";
    },

    getUserStatus() {
      if (!this.otherUser) return "Offline";
      if (this.otherUser.isOnline) return "Online";
      if (this.otherUser.lastSeen) {
        const now = new Date();
        const lastSeen = new Date(this.otherUser.lastSeen);
        const diff = (now - lastSeen) / 1000;
        if (diff < 60) return "Last seen just now";
        if (diff < 3600) return `Last seen ${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `Last seen ${Math.floor(diff / 3600)}h ago`;
        return `Last seen ${lastSeen.toLocaleDateString()}`;
      }
      return "Offline";
    },

    isOwnMessage(message) {
      if (!message) return false;

      const senderId =
        message.sender?._id ||
        message.fromUser ||
        message.senderID ||
        message.senderId;

      return String(senderId || "") === String(this.profile._id);
    },

    getMessageClasses(message) {
      return {
        "message-own": this.isOwnMessage(message),
        "message-other": !this.isOwnMessage(message)
      };
    },

    getMessageBubbleClasses(message) {
      return {
        "bubble-own": this.isOwnMessage(message),
        "bubble-other": !this.isOwnMessage(message)
      };
    },

    getMessageStatusIcon(message) {
      if (!this.isOwnMessage(message)) return "";

      if (message.read || (message.seenBy && message.seenBy.length > 0)) {
        return "mdi-check-all";
      }
      if (message.delivered || message.status === "delivered") {
        return "mdi-check";
      }
      if (message.status === "sent" || message.sent) {
        return "mdi-check";
      }
      return "mdi-check";
    },

    getMessageStatusColor(message) {
      if (!this.isOwnMessage(message)) return "";

      if (message.read || (message.seenBy && message.seenBy.length > 0)) {
        return "#4FC3F7";
      }
      if (message.delivered || message.status === "delivered") {
        return "#B0BEC5";
      }
      return "#B0BEC5";
    },

    getMessageStatusTooltip(message) {
      if (!this.isOwnMessage(message)) return "";

      if (message.read || (message.seenBy && message.seenBy.length > 0)) {
        return "Read";
      }
      if (message.delivered || message.status === "delivered") {
        return "Delivered";
      }
      return "Sent";
    },

    canEditMessage(message) {
      return this.editableMessageIds.has(message._id);
    },

    getMessageImages(message) {
      const images = [];
      if (message.attachments && Array.isArray(message.attachments)) {
        message.attachments.forEach(att => {
          // Only process image attachments
          const isImage = att.type === 'image' ||
            (att.mimetype && att.mimetype.startsWith('image/')) ||
            /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(att.url || '');
          if (isImage && att.url) {
            images.push(att.url);
          }
        });
      }
      if (message.images && Array.isArray(message.images)) {
        message.images.forEach(img => {
          if (typeof img === "string") images.push(img);
          else if (img && img.url) images.push(img.url);
        });
      }
      const tempId = message._id || `temp-${message.fromUser}-${message.date}`;
      if (this.pendingImages.has(tempId)) {
        const pending = this.pendingImages.get(tempId);
        if (Array.isArray(pending)) images.push(...pending);
        else images.push(pending);
      }
      return images.filter(Boolean);
    },

    getImageGridClass(count) {
      if (count === 1) return "grid-single";
      if (count === 2) return "grid-two";
      if (count === 3) return "grid-three";
      return "grid-multiple";
    },

    getImageClass(totalCount, index) {
      if (totalCount === 1) return "img-large";
      if (totalCount === 2 || totalCount === 3) return "img-medium";
      if (totalCount > 3 && index >= 3) return "img-hidden";
      return "img-small";
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (date.toDateString() === today.toDateString()) return "Today";
      if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
      return date.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    },

    formatMessageTime(date) {
      if (!date) return "";
      return new Date(date).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    onScroll() {
      const container = this.$refs.messagesContainer;
      if (!container) return;
      const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
      if (nearBottom) this.markMessagesAsRead();
    },

    selectEmoji(emoji) {
      this.insertTextAtCursor(emoji.data);
      this.emojiMenu = false;
    },

    insertTextAtCursor(text) {
      const textarea = this.$refs.messageInput?.$refs?.input;
      if (!textarea) return;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      this.newMessage = this.newMessage.substring(0, start) + text + this.newMessage.substring(end);
      this.$nextTick(() => {
        textarea.setSelectionRange(start + text.length, start + text.length);
        textarea.focus();
      });
    },

    addNewLine() {
      this.insertTextAtCursor("\n");
    },

    triggerGallery() {
      this.$refs.fileInput.setAttribute("multiple", "multiple");
      this.$refs.fileInput.click();
      this.attachmentMenu = false;
    },

    triggerCamera() {
      this.$refs.cameraInput.removeAttribute("multiple");
      this.$refs.cameraInput.click();
      this.attachmentMenu = false;
    },

    triggerDocument() {
      this.$refs.documentInput.click();
      this.attachmentMenu = false;
    },

    // In ChatMessages.vue methods

    onFileSelected(event) {
      const files = Array.from(event.target.files);
      if (files.length === 0) return;

      const maxFiles = 10;
      const maxSize = 25 * 1024 * 1024; // 25MB

      if (this.selectedFiles.length + files.length > maxFiles) {
        this.showSnackbar(`You can only attach up to ${maxFiles} files at a time`, "error", "mdi-alert");
        return;
      }

      files.forEach(file => {
        // Check file size
        if (file.size > maxSize) {
          this.showSnackbar(`${file.name} is too large (max 25MB)`, "error", "mdi-alert");
          return;
        }

        this.selectedFiles.push(file);

        // Handle different file types
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.selectedImages.push({
              id: Date.now() + Math.random(),
              file: file,
              preview: e.target.result,
              name: file.name,
              size: file.size,
              type: 'image'
            });
          };
          reader.readAsDataURL(file);
        } else if (file.type.startsWith("video/")) {
          // For videos, create a thumbnail or use a video icon
          this.selectedImages.push({
            id: Date.now() + Math.random(),
            file: file,
            preview: require('@/assets/default.jpg'), // You'll need to add this icon
            name: file.name,
            size: file.size,
            type: 'video'
          });
        } else {
          // For documents, use a document icon
          this.selectedImages.push({
            id: Date.now() + Math.random(),
            file: file,
            preview: require('@/assets/default.jpg'), // You'll need to add this icon
            name: file.name,
            size: file.size,
            type: 'document'
          });
        }
      });

      event.target.value = "";
    },

    removeAttachment(index) {
      if (index !== undefined) {
        this.selectedFiles.splice(index, 1);
        this.selectedImages.splice(index, 1);
      }
    },

    removeAllAttachments() {
      this.selectedFiles = [];
      this.selectedImages = [];
    },

    onTyping() {
      if (!this.singleRoom?._id || !this.profile?._id) return;

      if (!this.isTyping) {
        this.isTyping = true;
        const myName = (
          (this.profile.first_name || '') + ' ' + (this.profile.last_name || '')
        ).trim() || this.profile.name || this.profile.displayName || this.profile.email?.split('@')[0] || 'User';

        socket.sendTypingStart(
          String(this.singleRoom._id),
          String(this.profile._id),
          myName
        );
      }

      clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(() => this.stopTyping(), 2000);
    },

    stopTyping() {
      if (!this.isTyping) return;
      this.isTyping = false;
      if (!this.singleRoom?._id || !this.profile?._id) return;
      socket.sendTypingStop(String(this.singleRoom._id), String(this.profile._id));
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
        this.typingTimeout = null;
      }
    },

    handleTypingStart(data) {
      // Backend sends chatId (not roomID)
      const chatId = data?.chatId || data?.roomID;
      const fromUser = String(data?.fromUser || "");
      let fromUserName = data?.fromUserName || "";

      if (!chatId || !fromUser) return;
      // Don't show typing for my own socket echo
      if (fromUser === String(this.profile?._id)) return;
      // Only care about the active chat
      if (String(chatId) !== String(this.singleRoom?._id)) return;

      // Resolve name from group members list if backend didn't send it
      if (!fromUserName) {
        const members = this.singleRoom?.members || this.singleRoom?.participants || [];
        const member = members.find(m => {
          const uid = String(m._id || m.userID || m.user?._id || "");
          return uid === fromUser;
        });
        if (member) {
          fromUserName = (
            (member.first_name || '') + ' ' + (member.last_name || '')
          ).trim() || member.name || member.displayName || member.email?.split('@')[0] || 'User';
        } else {
          fromUserName = 'Someone';
        }
      }

      if (!this.typingUsers) this.$set(this, 'typingUsers', {});
      this.$set(this.typingUsers, fromUser, {
        userId: fromUser,
        name: fromUserName,
        chatId,
        timestamp: Date.now()
      });

      // Auto-expire after 5 s in case typing-stop never arrives
      if (!this.typingTimeouts) this.typingTimeouts = {};
      if (this.typingTimeouts[fromUser]) clearTimeout(this.typingTimeouts[fromUser]);
      this.typingTimeouts[fromUser] = setTimeout(() => {
        this.$delete(this.typingUsers, fromUser);
        delete this.typingTimeouts[fromUser];
      }, 5000);
    },

    handleTypingStop(data) {
      const fromUser = String(data?.fromUser || "");
      if (!fromUser) return;
      if (this.typingUsers) this.$delete(this.typingUsers, fromUser);
      if (this.typingTimeouts?.[fromUser]) {
        clearTimeout(this.typingTimeouts[fromUser]);
        delete this.typingTimeouts[fromUser];
      }
    },

    // Open full emoji picker
    openFullEmojiPicker(message) {
      if (!message) return;

      // Store the message reference
      this.currentSelectedMessage = message;
      this.showFullEmojiPicker = true;

      // Close any open reaction menus
      const messageId = message._id || message.clientMessageId;
      Object.keys(this.reactionMenu).forEach(key => {
        if (key !== messageId) {
          this.$set(this.reactionMenu, key, false);
        }
      });

      // Log for debugging
      console.log('Opening emoji picker for message:', message._id);
    },

    // Initialize the emoji picker


    // Select emoji from the picker
    async selectEmojiFromPicker(emoji) {
      console.log('Emoji selected from picker:', emoji);
      console.log('Current selected message:', this.currentSelectedMessage);

      if (!this.currentSelectedMessage) {
        console.warn('No message selected for reaction');
        this.showSnackbar("Please select a message first", "warning", "mdi-alert");
        return;
      }

      // Get the emoji data - handle different emoji formats
      let emojiChar = emoji;
      if (emoji && typeof emoji === 'object') {
        // If emoji object has data property (like from VEmojiPicker)
        emojiChar = emoji.data || emoji.emoji || emoji;
      }

      if (!emojiChar) {
        console.warn('Invalid emoji selected:', emoji);
        return;
      }

      // Close the picker first
      this.closeFullEmojiPicker();

      // Add the reaction - pass the message and emoji
      await this.toggleReaction(this.currentSelectedMessage, emojiChar);
    },


    // Close full emoji picker
    closeFullEmojiPicker() {
      this.showFullEmojiPicker = false;
      // Don't clear currentSelectedMessage immediately - let the picker close animation finish
      setTimeout(() => {
        this.currentSelectedMessage = null;
      }, 300);
    },


    // Remove current reaction
    async removeCurrentReaction() {
      if (!this.currentSelectedMessage) return;
      const currentEmoji = this.getUserReaction(this.currentSelectedMessage);
      if (currentEmoji) {
        await this.toggleReaction(this.currentSelectedMessage, currentEmoji);
      }
    },


    // Check if current user has ANY reaction on this message
    hasAnyUserReaction(message) {
      if (!message || !message.reactions) return false;
      const currentUserId = this.profile._id;
      return message.reactions.some(r =>
        r.users && r.users.some(u => u._id === currentUserId || u._id?.toString() === currentUserId?.toString())
      );
    },

    // Get the user's current reaction emoji
    getUserReaction(message) {
      if (!message || !message.reactions) return null;
      const currentUserId = this.profile._id;
      for (const reaction of message.reactions) {
        if (reaction.users && reaction.users.some(u => u._id === currentUserId || u._id?.toString() === currentUserId?.toString())) {
          return reaction.emoji;
        }
      }
      return null;
    },

    sanitizeInput(value) {
      if (!value) return value;
      let clean = DOMPurify.sanitize(value, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
      if (!clean.trim()) {
        const temp = document.createElement("div");
        temp.innerHTML = value;
        clean = temp.textContent || temp.innerText || "";
      }
      return clean.trim();
    },
    triggerVideo() {
      this.$refs.videoInput = this.$refs.videoInput || document.createElement('input');
      const videoInput = this.$refs.videoInput;
      videoInput.type = 'file';
      videoInput.accept = 'video/*';
      videoInput.multiple = true;
      videoInput.onchange = this.onFileSelected;
      videoInput.click();
      this.attachmentMenu = false;
    },

    // In ChatMessages.vue

    async sendMessage() {
      // If no content to send, return
      if (!this.newMessage.trim() && this.selectedFiles.length === 0 && this.selectedImages.length === 0) {
        return;
      }

      if (this.sendingMessage) return;
      if (!this.profile?._id) {
        this.showSnackbar("Session expired. Please refresh.", "error", "mdi-alert");
        return;
      }
      // Check file sizes before sending
      for (const file of this.selectedFiles) {
        if (file.size > 25 * 1024 * 1024) { // 25MB limit
          this.showSnackbar(`File ${file.name} is too large (max 25MB)`, "error", "mdi-alert");
          return;
        }
      }

      const clientMessageId = `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const text = this.sanitizeInput(this.newMessage.trim());

      // Get participant info
      const participant = this.singleRoom?.selectedUser ||
        this.otherUser ||
        this.singleRoom?.participants?.find(p => String(p._id) !== String(this.profile?._id));

      const participantId = participant?._id || participant?.id;
      const participantType = participant?.userType || participant?.type || "User";

      // Determine message type
      let messageType = "text";
      if (this.selectedFiles.length > 0) {
        const hasImages = this.selectedFiles.some(file => file.type.startsWith("image/"));
        const hasVideos = this.selectedFiles.some(file => file.type.startsWith("video/"));
        const hasDocuments = this.selectedFiles.some(file =>
          file.type.startsWith("application/") ||
          file.type.startsWith("text/") ||
          file.name.endsWith('.pdf') ||
          file.name.endsWith('.doc') ||
          file.name.endsWith('.docx')
        );

        if (hasImages && !hasVideos && !hasDocuments) {
          messageType = "image";
        } else if (hasVideos) {
          messageType = "video";
        } else if (hasDocuments) {
          messageType = "document";
        } else {
          messageType = "file";
        }
      }

      this.sendingMessage = true;

      try {
        const isTempChat = String(this.singleRoom?._id || "").startsWith("temp-");
        let tempMessage = null;

        // If it's a temp chat, add local message first
        if (isTempChat) {
          tempMessage = {
            _id: clientMessageId,
            clientMessageId,
            sender: {
              _id: this.profile?._id,
              name: this.profile?.name || this.profile?.first_name || "User",
              userType: this.profile?.type || "User"
            },
            messageType: messageType,
            text: text,
            status: "sending",
            sending: true,
            createdAt: new Date().toISOString(),
            delivered: false,
            read: false,
            // Add a flag to identify this as a local temp message
            isTemp: true
          };

          // Add reply data if replying
          if (this.replyToMessageData) {
            tempMessage.replyTo = {
              messageId: this.replyToMessageData._id,
              text: this.replyToMessageData.text || this.replyToMessageData.message || "",
              senderName: this.getReplySenderName(this.replyToMessageData),
              messageType: this.replyToMessageData.messageType || "text"
            };
          }

          // Add image previews to local message if any
          if (this.selectedImages.length > 0) {
            tempMessage.attachments = this.selectedImages.map(img => ({
              name: img.name,
              type: img.file.type,
              url: img.preview,
              size: img.size,
              thumbnailUrl: img.preview
            }));
          }

          // this.$store.commit("chat/ADD_LOCAL_MESSAGE", {
          //   roomId: this.singleRoom._id,
          //   message: tempMessage
          // });

          // // Also add to local messages array
          // if (this.singleRoom && this.singleRoom.messages) {
          //   this.singleRoom.messages.push(tempMessage);
          // }
        }

        // Prepare payload
        let payload;
        const isReplying = this.replyToMessageData !== null;

        if (this.selectedFiles.length > 0) {
          const formData = new FormData();

          if (isReplying) {
            formData.append("chatId", this.singleRoom?._id || '');
            formData.append("replyToMessageId", this.replyToMessageData._id);
            formData.append("messageType", messageType);
            formData.append("text", text);
            formData.append("clientMessageId", clientMessageId);
            if (this.singleRoom?._id && !String(this.singleRoom._id).startsWith("temp-")) {
              formData.append("chatId", this.singleRoom._id);
            }
          } else {
            formData.append("participantId", participantId);
            formData.append("participantType", participantType);
            formData.append("messageType", messageType);
            formData.append("text", text);
            formData.append("clientMessageId", clientMessageId);
            if (this.singleRoom?._id && !String(this.singleRoom._id).startsWith("temp-")) {
              formData.append("chatId", this.singleRoom._id);
            } else {
              formData.append("tempRoomId", this.singleRoom?._id);
            }
          }

          this.selectedFiles.forEach((file) => {
            formData.append("files", file);
          });

          payload = formData;
        } else {
          if (isReplying) {
            payload = {
              chatId: this.singleRoom?._id || '',
              replyToMessageId: this.replyToMessageData._id,
              messageType: messageType,
              text: text,
              clientMessageId: clientMessageId
            };
          } else {
            payload = {
              participantId,
              participantType,
              messageType,
              text,
              clientMessageId,
              tempRoomId: this.singleRoom?._id && !String(this.singleRoom._id).startsWith("temp-")
                ? undefined
                : this.singleRoom?._id
            };

            if (this.singleRoom?._id && !String(this.singleRoom._id).startsWith("temp-")) {
              payload.chatId = this.singleRoom._id;
            }
          }
        }

        // Send the message
        let result;
        if (isReplying) {
          result = await this.$store.dispatch("chat/replyToMessage", payload);
        } else {
          result = await this.$store.dispatch("chat/addNewMessage", payload);
        }

        const serverChat = result?.chat;
        const serverMessage = result?.message;

        // Handle server response
        if (serverChat) {
          let existingMessages = Array.isArray(this.singleRoom?.messages) ? this.singleRoom.messages : [];

          // Remove the temporary message if it exists
          if (isTempChat) {
            existingMessages = existingMessages.filter(m => m._id !== clientMessageId);
          }

          const rawMessages = serverChat.messages && serverChat.messages.length > 0
            ? serverChat.messages
            : serverMessage
              ? [...existingMessages, serverMessage]
              : existingMessages;

          // Deduplicate — socket event may have already added this message before
          // the HTTP response was processed (race condition)
          const seenIds = new Set();
          const dedupedMessages = rawMessages.filter(m => {
            if (!m) return false;
            const id = String(m._id || m.clientMessageId || '');
            if (!id) return true;
            if (seenIds.has(id)) return false;
            seenIds.add(id);
            return true;
          });

          const mergedChat = {
            ...serverChat,
            messages: dedupedMessages,
            selectedUser: this.singleRoom?.selectedUser
          };

          // Replace temp chat or update existing
          if (isTempChat) {
            this.$store.commit("chat/REPLACE_TEMP_CHAT", {
              tempId: this.singleRoom._id,
              newChat: mergedChat
            });
          }

          this.$store.commit("chat/setSingleRoom", mergedChat);

          // Update URL if needed
          if (isTempChat && mergedChat._id) {
            this.$router.replace({
              name: "chat-details",
              query: { chatId: mergedChat._id }
            }).catch(err => {
              if (err.name !== 'NavigationDuplicated') {
                console.error('Navigation error:', err);
              }
            });
          }
          if (serverMessage && mergedChat._id) {
            this.showSnackbar("Message sent", "success", "mdi-check");
          }

          // Emit socket event for new message
          if (serverMessage && mergedChat._id) {
            try {
              socket.sendNewMessage({ chatId: mergedChat._id, message: serverMessage });

              setTimeout(() => {
                socket.sendMessageDelivered(
                  mergedChat._id,
                  serverMessage._id,
                  { _id: serverMessage.sender?._id, userType: serverMessage.sender?.userType }
                );
              }, 1000);
            } catch (socketError) {
              console.warn('Socket emit error:', socketError);
            }

            this.showSnackbar("Message sent", "success", "mdi-check");
          }
        }

        // Clear attachments and reply after successful send
        this.newMessage = "";
        this.selectedFiles = [];
        this.selectedImages = [];
        this.replyToMessageData = null;

        this.$nextTick(() => {
          this.scrollToBottom();
        });

      } catch (err) {
        console.error("Error sending message:", err);

        this.$store.commit("chat/MARK_MESSAGE_FAILED", {
          roomId: this.singleRoom._id,
          tempId: clientMessageId
        });

        if (this.singleRoom && this.singleRoom.messages) {
          const failedMessage = this.singleRoom.messages.find(m => m._id === clientMessageId || m.clientMessageId === clientMessageId);
          if (failedMessage) {
            failedMessage.failed = true;
            failedMessage.sending = false;
            failedMessage.status = 'failed';
            failedMessage.error = err.message || 'Failed to send';
          }
        }

        this.showSnackbar(err.message || "Failed to send message", "error", "mdi-alert");
      } finally {
        this.sendingMessage = false;
      }
    },


    retryMessage(message) {
      if (!message.failed) return;

      const index = this.singleRoom.messages.findIndex(msg =>
        msg._id === message._id || msg.clientMessageId === message.clientMessageId
      );

      if (index !== -1) {
        this.$set(this.singleRoom.messages[index], 'failed', false);
        this.$set(this.singleRoom.messages[index], 'sending', true);
        this.$set(this.singleRoom.messages[index], 'status', 'sending');

        const textToSend = message.text || message.message || "";
        const clientMessageId = message.clientMessageId || `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        const payload = {
          chatId: this.singleRoom._id,
          messageType: "text",
          text: textToSend,
          clientMessageId: clientMessageId
        };

        if (message.replyTo) {
          payload.replyTo = message.replyTo;
        }

        this.$store.dispatch("chat/addNewMessage", payload)
          .then(response => {
            if (response && response.success && response.message) {
              const actualMessage = response.message;
              const msgIndex = this.singleRoom.messages.findIndex(
                msg => msg.clientMessageId === clientMessageId
              );
              if (msgIndex !== -1) {
                this.$set(this.singleRoom.messages, msgIndex, {
                  ...actualMessage,
                  sending: false,
                  failed: false,
                  sent: true,
                  status: "sent"
                });
              }
              this.$store.dispatch("chat/getAllChats");
              this.showSnackbar("Message sent successfully", "success", "mdi-check");
            }
          })
          .catch(err => {
            console.error("Failed to retry message:", err);
            const msgIndex = this.singleRoom.messages.findIndex(
              msg => msg.clientMessageId === clientMessageId
            );
            if (msgIndex !== -1) {
              this.$set(this.singleRoom.messages[msgIndex], 'sending', false);
              this.$set(this.singleRoom.messages[msgIndex], 'failed', true);
            }
          });
      }
    },

    startVoiceMessage() {
      console.log("Starting voice message...");
    },

    replyToMessage(message) {
      // Don't allow replying to your own message in a temp chat
      if (String(this.singleRoom?._id || "").startsWith("temp-") && this.isOwnMessage(message)) {
        this.showSnackbar("Cannot reply to your own message in a new chat", "warning", "mdi-alert");
        return;
      }

      // Store the message to reply to
      this.replyToMessageData = message;

      // Focus the input
      this.$refs.messageInput?.focus();

      // Show a snackbar notification
      const senderName = this.getReplySenderName(message);
      this.showSnackbar(`Replying to ${senderName}`, "info", "mdi-reply");

      // Scroll to the message if it's not visible
      this.scrollToMessage(message._id);
    },

    // Add this helper method
    scrollToMessage(messageId) {
      // Find the message element and scroll to it
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        const messageElement = container?.querySelector(`[data-message-id="${messageId}"]`);
        if (messageElement) {
          messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },
    // In ChatMessages.vue - methods

    getSystemMessageText(message) {
      if (!message) return 'System notification';

      // PRIORITY 1: Use the text field if it exists and is not empty
      if (message.text && message.text.trim()) {
        return message.text;
      }

      // PRIORITY 2: Use systemMeta summary
      if (message.systemMeta?.summary) {
        return message.systemMeta.summary;
      }

      // PRIORITY 3: Generate from systemMeta action
      if (message.systemMeta?.action) {
        const action = message.systemMeta.action;
        const actorName = message.sender?.name || message.sender?.firstName || 'Someone';
        const targetName = message.systemMeta.targetUserName ||
          message.systemMeta.removedUser?.name ||
          message.systemMeta.addedUser?.name ||
          '';

        switch (action) {
          case 'member_joined':
            return `✨ ${targetName || actorName} joined the group`;
          case 'member_left':
            return `👋 ${targetName || actorName} left the group`;
          case 'member_added':
            return `${actorName} added ${targetName || 'a member'} to the group`;
          case 'member_removed':
            return `${actorName} removed ${targetName || 'a member'} from the group`;
          case 'admin_added':
            return `${actorName} made ${targetName || 'a member'} a group admin`;
          case 'admin_removed':
            return `${actorName} removed ${targetName || 'a member'} as group admin`;
          case 'group_created':
            return `📌 Group created by ${actorName}`;
          case 'name_changed':
            return `📝 ${actorName} changed group name to "${message.systemMeta.newValue || ''}"`;
          case 'image_changed':
            return `🖼️ ${actorName} updated group image`;
          case 'group_updated':
            return `⚙️ ${actorName} updated group settings`;
          default:
            return message.text || message.message || 'System notification';
        }
      }

      // Fallback
      return message.text || message.message || 'System notification';
    },
    clearReply() {
      this.replyToMessageData = null;
    },

    getReplySenderName(message) {
      if (!message) return "Unknown";

      // If message has senderName directly
      if (message.senderName) return message.senderName;

      // If message has sender object
      if (message.sender) {
        if (message.sender.name) return message.sender.name;
        if (message.sender.first_name) {
          return `${message.sender.first_name} ${message.sender.last_name || ''}`.trim();
        }
      }

      // If it's the current user's message
      if (this.isOwnMessage(message)) return "You";

      // If message has name directly
      if (message.name) return message.name;
      if (message.first_name) {
        return `${message.first_name} ${message.last_name || ''}`.trim();
      }

      // Fallback: try to get from other user
      if (this.otherUser) {
        return this.getDisplayName();
      }

      return "User";
    },

    getReplyPreview(message) {
      if (!message) return "";

      // If it's a text message
      if (message.text) {
        return message.text.length > 60 ? message.text.slice(0, 60) + '...' : message.text;
      }

      // If it's a message with text field
      if (message.message) {
        return message.message.length > 60 ? message.message.slice(0, 60) + '...' : message.message;
      }

      // If it's an image message
      if (message.attachments && message.attachments.length > 0) {
        const imageAttachments = message.attachments.filter(att => att.type && att.type.startsWith('image/'));
        if (imageAttachments.length > 0) {
          return `📷 ${imageAttachments.length} photo(s)`;
        }
        return `📎 ${message.attachments.length} attachment(s)`;
      }

      // If it's a file message
      if (message.messageType === 'image') return '📷 Photo';
      if (message.messageType === 'video') return '🎬 Video';
      if (message.messageType === 'document') return '📄 Document';
      if (message.messageType === 'file') return '📎 File';

      return 'Message';
    },

    isReplyMessage(message) {
      return message.replyTo && message.replyTo.messageId;
    },


    // Toggle reaction on a message

    async toggleReaction(message, emoji) {
      try {
        if (!message) {
          console.error('No message provided for reaction');
          this.showSnackbar("Message not found", "error", "mdi-alert");
          return;
        }

        if (!emoji) {
          console.error('No emoji provided for reaction');
          this.showSnackbar("Please select an emoji", "warning", "mdi-alert");
          return;
        }

        const messageId = message._id || message.clientMessageId;
        const chatId = this.singleRoom._id;

        if (!chatId) {
          console.error('No chat ID found');
          this.showSnackbar("Chat not found", "error", "mdi-alert");
          return;
        }

        // Set loading state
        const loadingKey = messageId;
        this.$set(this.reactionLoading, loadingKey, true);

        // Close the reaction picker
        this.$set(this.reactionMenu, messageId, false);

        // Check if user already reacted with this emoji
        const hasReacted = this.hasUserReacted(message, emoji);
        const action = hasReacted ? 'remove' : 'add';

        console.log('Toggling reaction:', { messageId, chatId, emoji, action });

        // Call the API
        const result = await this.$store.dispatch("chat/addReaction", {
          messageId: message._id,
          chatId: chatId,
          emoji: emoji
        });

        console.log('Reaction API result:', result);

        if (result && result.success) {
          // Update local state with the response
          if (result.reactions) {
            message.reactions = result.reactions;
          } else {
            // Manually update reactions if API doesn't return full reactions
            if (!message.reactions) {
              message.reactions = [];
            }

            if (action === 'add') {
              // Check if reaction with this emoji exists
              const existingReaction = message.reactions.find(r => r.emoji === emoji);
              if (existingReaction) {
                // Add user to existing reaction
                if (!existingReaction.users) {
                  existingReaction.users = [];
                }
                // Check if user already exists in this reaction
                const userExists = existingReaction.users.some(
                  u => u._id === this.profile._id || u._id?.toString() === this.profile._id?.toString()
                );
                if (!userExists) {
                  existingReaction.users.push({
                    _id: this.profile._id,
                    userType: this.profile.type || 'User',
                    name: this.profile.name || this.profile.first_name || 'User',
                    reactedAt: new Date()
                  });
                }
              } else {
                // Create new reaction
                message.reactions.push({
                  emoji: emoji,
                  users: [{
                    _id: this.profile._id,
                    userType: this.profile.type || 'User',
                    name: this.profile.name || this.profile.first_name || 'User',
                    reactedAt: new Date()
                  }]
                });
              }
            } else {
              // Remove reaction (action === 'remove')
              const reactionIndex = message.reactions.findIndex(r => r.emoji === emoji);
              if (reactionIndex !== -1) {
                const reaction = message.reactions[reactionIndex];
                if (reaction.users) {
                  // Remove user from reaction
                  reaction.users = reaction.users.filter(
                    u => u._id !== this.profile._id && u._id?.toString() !== this.profile._id?.toString()
                  );
                  // If no users left, remove the reaction
                  if (reaction.users.length === 0) {
                    message.reactions.splice(reactionIndex, 1);
                  }
                }
              }
            }
          }

          // Emit socket event for reaction
          try {
            socket.sendReaction(
              chatId,
              message._id,
              emoji,
              {
                _id: this.profile._id,
                userType: this.profile.type || 'User',
                name: this.profile.name || this.profile.first_name || 'User'
              },
              action
            );
          } catch (socketError) {
            console.warn('Socket reaction emit error:', socketError);
            // Don't fail the whole operation if socket emit fails
          }

          // Force update the view
          this.$forceUpdate();

          // Show success message
          this.showSnackbar(
            action === 'add' ? 'Reaction added' : 'Reaction removed',
            'success',
            action === 'add' ? 'mdi-emoticon-happy' : 'mdi-emoticon-sad'
          );

          // Check if we need to update the full emoji picker
          if (this.showFullEmojiPicker && this.currentSelectedMessage?._id === message._id) {
            this.currentSelectedMessage = message;
          }

        } else {
          this.showSnackbar(result?.message || "Failed to update reaction", "error", "mdi-alert");
        }
      } catch (error) {
        console.error("Error toggling reaction:", error);
        this.showSnackbar(error.message || "Failed to update reaction", "error", "mdi-alert");
      } finally {
        // Clear loading state
        const messageId = message._id || message.clientMessageId;
        this.$set(this.reactionLoading, messageId, false);
      }
    },

    // Helper method to check if user reacted with a specific emoji
    hasUserReacted(message, emoji) {
      if (!message || !message.reactions) return false;

      const currentUserId = this.profile?._id;
      if (!currentUserId) return false;

      const reaction = message.reactions.find(r => r.emoji === emoji);
      if (!reaction || !reaction.users) return false;

      return reaction.users.some(
        u => u._id === currentUserId || u._id?.toString() === currentUserId?.toString()
      );
    },

    // Helper method to get unique reactions with counts
    getUniqueReactions(message) {
      if (!message || !message.reactions || message.reactions.length === 0) return [];

      // Filter out reactions with empty users array
      const validReactions = message.reactions.filter(r => r.users && r.users.length > 0);

      return validReactions.map(reaction => ({
        emoji: reaction.emoji,
        users: reaction.users || [],
        count: reaction.users ? reaction.users.length : 0
      }));
    },

    // Helper method to get reaction count for a specific emoji
    getReactionCount(message, emoji) {
      if (!message || !message.reactions) return 0;
      const reaction = message.reactions.find(r => r.emoji === emoji);
      return reaction && reaction.users ? reaction.users.length : 0;
    },

    // Helper method to get tooltip showing who reacted
    getReactionTooltip(message, emoji) {
      if (!message || !message.reactions) return '';

      const reaction = message.reactions.find(r => r.emoji === emoji);
      if (!reaction || !reaction.users || reaction.users.length === 0) return '';

      const userNames = reaction.users.map(u => {
        if (u._id === this.profile?._id) return 'You';
        return u.name || u.userName || 'User';
      });

      if (userNames.length === 0) return '';
      if (userNames.length === 1) return userNames[0];
      if (userNames.length === 2) return `${userNames[0]} and ${userNames[1]}`;
      return `${userNames[0]}, ${userNames[1]} and ${userNames.length - 2} more`;
    },



    // Check if a reaction should show as selected (current user reacted)
    isReactionSelected(message, emoji) {
      return this.hasUserReacted(message, emoji);
    },

    // Long press or hover to show reaction picker
    handleMessageLongPress(message) {
      // Close any other open reaction pickers
      Object.keys(this.reactionMenu).forEach(key => {
        if (key !== (message._id || message.clientMessageId)) {
          this.$set(this.reactionMenu, key, false);
        }
      });

      // Toggle the reaction picker for this message
      const messageId = message._id || message.clientMessageId;
      this.$set(this.reactionMenu, messageId, !this.reactionMenu[messageId]);
    },



    editMessage(message) {
      if (!message.text && !message.message) {
        this.showSnackbar("Cannot edit message with no text", "warning", "mdi-alert");
        return;
      }
      if (!this.isOwnMessage(message)) {
        this.showSnackbar("You can only edit your own messages", "error", "mdi-alert");
        return;
      }
      this.editingMessage = message;
      this.editMessageDialog = true;
    },

    async handleSaveEditedMessage({ message, newText }) {
      try {
        const payload = { chatId: this.singleRoom._id, messageId: message._id, newText: newText };
        const messageIndex = this.singleRoom.messages.findIndex(msg => msg._id === message._id);
        if (messageIndex !== -1) {
          this.singleRoom.messages[messageIndex].text = newText;
          this.singleRoom.messages[messageIndex].edited = { isEdited: true };
          this.singleRoom.messages[messageIndex].editedAt = new Date();
        }
        await this.editSingleMessage(payload);
        this.showSnackbar("Message updated successfully", "success", "mdi-check");
        this.editMessageDialog = false;
        this.editingMessage = null;
      } catch (error) {
        console.error("Error editing message:", error);
        this.showSnackbar("Failed to edit message", "error", "mdi-alert");
      }
    },

    handleCancelEditMessage() {
      this.editMessageDialog = false;
      this.editingMessage = null;
    },

    copyMessage(message) {
      const textToCopy = message.text || message.message || "";
      if (!textToCopy) {
        this.showSnackbar("No text to copy", "warning", "mdi-alert");
        return;
      }
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(textToCopy)
          .then(() => this.showSnackbar("Message copied to clipboard", "success", "mdi-content-copy"))
          .catch(() => this.fallbackCopyText(textToCopy));
      } else {
        this.fallbackCopyText(textToCopy);
      }
    },

    fallbackCopyText(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        if (document.execCommand("copy")) {
          this.showSnackbar("Message copied to clipboard", "success", "mdi-content-copy");
        } else {
          throw new Error("Copy failed");
        }
      } catch (err) {
        this.showSnackbar("Failed to copy message", "error", "mdi-alert");
      } finally {
        document.body.removeChild(textArea);
      }
    },

    deleteMessage(message) {
      this.confirmDeleteMessage = message;
      this.deleteMessageDialog = true;
      // Set a flag to indicate which action is being performed
      this.deleteAction = 'me'; // 'me' or 'everyone'
    },

    // This method is called when user clicks "Delete for everyone"
    deleteMessageForEveryoneAction(message) {
      this.confirmDeleteMessage = message;
      this.deleteMessageDialog = true;
      this.deleteAction = 'everyone';
    },

    async confirmDelete() {
      if (!this.confirmDeleteMessage) return;

      if (this.deleteAction === 'everyone') {
        // Delete for everyone
        this.deletingMessage = true;
      } else {
        // Delete for me
        this.deletingForMe = true;
      }

      try {
        let result;
        const payload = {
          chatId: this.singleRoom._id,
          messageId: this.confirmDeleteMessage._id
        };

        if (this.deleteAction === 'everyone') {
          result = await this.deleteMessageForEveryone(payload);
        } else {
          result = await this.deleteSingleMessage(payload);
        }

        if (result && result.success) {
          if (this.deleteAction === 'everyone') {
            // Update local state for "delete for everyone"
            const messageIndex = this.singleRoom.messages.findIndex(
              msg => msg._id === this.confirmDeleteMessage._id
            );
            if (messageIndex !== -1) {
              // NOTE: isDeleted must stay false — the backend converts this into a
              // visible system message rather than hiding it. Setting it to true
              // would cause filteredRoomMessages to filter it out entirely.
              this.$set(this.singleRoom.messages[messageIndex], 'isDeleted', false);
              this.$set(this.singleRoom.messages[messageIndex], 'text', 'This message was deleted');
              this.$set(this.singleRoom.messages[messageIndex], 'attachments', []);
              this.$set(this.singleRoom.messages[messageIndex], 'messageType', 'system');
              this.$set(this.singleRoom.messages[messageIndex], 'systemMeta', {
                action: 'message_deleted',
                summary: 'Message was deleted'
              });
              this.$forceUpdate();
            }
            this.showSnackbar("Message deleted for everyone", "success", "mdi-check");
          } else {
            // Update local state for "delete for me"
            const messageIndex = this.singleRoom.messages.findIndex(
              msg => msg._id === this.confirmDeleteMessage._id
            );
            if (messageIndex !== -1) {
              if (!this.singleRoom.messages[messageIndex].deletedFor) {
                this.$set(this.singleRoom.messages[messageIndex], 'deletedFor', []);
              }
              this.singleRoom.messages[messageIndex].deletedFor.push({
                _id: this.profile._id,
                userType: this.profile.type || 'User'
              });
            }
            this.singleRoom.messages = this.singleRoom.messages.filter(
              msg => !(msg._id === this.confirmDeleteMessage._id &&
                msg.deletedFor?.some(d => d._id === this.profile._id))
            );
            this.showSnackbar("Message deleted for you", "success", "mdi-check");
          }
        } else {
          throw new Error(result?.message || "Failed to delete message");
        }
      } catch (error) {
        console.error("Error deleting message:", error);
        this.showSnackbar(error.message || "Failed to delete message", "error", "mdi-alert");
      } finally {
        this.deleteMessageDialog = false;
        this.confirmDeleteMessage = null;
        this.deletingMessage = false;
        this.deletingForMe = false;
        this.deleteAction = 'me';
      }
    },

    cancelDeleteMessage() {
      this.deleteMessageDialog = false;
      this.confirmDeleteMessage = null;
      this.deletingForMe = false;
      this.deletingMessage = false;
      this.deleteAction = 'me';
    },


    clearChatMessages() {
      this.clearChatDialog = true;
    },

    async confirmClearChat() {
      this.clearingChat = true;
      this.clearChatDialog = false;

      const roomId = this.singleRoom._id;
      try {
        // Clear messages locally first for better UX
        this.$store.commit("chat/clearRoomMessages", roomId);
        if (this.singleRoom && this.singleRoom._id === roomId) {
          this.$set(this.singleRoom, "messages", []);
        }
        this.$forceUpdate();

        // Call the API with the correct payload
        const payload = {
          chatId: roomId,
          clearForEveryone: this.clearForEveryone || false
        };

        const result = await this.clearChat(payload);

        if (result && result.success) {
          this.showSnackbar(
            this.clearForEveryone ? "Chat cleared for everyone" : "Chat cleared successfully",
            "success",
            "mdi-check"
          );

          // Reset the flag
          this.clearForEveryone = false;

          // Refresh the chat data to get latest state
          await this.$store.dispatch('chat/getAllChats');

          // Reload current chat
          if (roomId) {
            const freshChat = await this.$store.dispatch('chat/getChatById', roomId);
            if (freshChat) {
              this.$store.commit('chat/setSingleRoom', freshChat);
            }
          }
        } else {
          throw new Error(result?.message || "Failed to clear chat");
        }
      } catch (error) {
        console.error("Error clearing chat:", error);
        this.showSnackbar(error.message || "Failed to clear chat", "error", "mdi-alert");

        // Restore chat data if API failed
        await this.$store.dispatch('chat/getAllChats');
        const currentRoom = this.$store.getters["chat/getChats"].find(chat => chat._id === roomId);
        if (currentRoom) {
          this.$store.commit("chat/setSingleRoom", currentRoom);
        }
      } finally {
        this.clearingChat = false;
        this.clearForEveryone = false;
      }
    },

    toggleSearch() {
      this.showSearch = !this.showSearch;
      if (this.showSearch) {
        // Focus the search input when opened
        this.$nextTick(() => {
          const searchInput = this.$refs.searchInput?.$el?.querySelector('input');
          if (searchInput) {
            searchInput.focus();
          }
        });
      } else {
        // Clear search when closed
        this.searchQuery = "";
        clearTimeout(this.searchTimeout);
      }
    },

    closeSearch() {
      this.showSearch = false;
      this.searchQuery = "";
      clearTimeout(this.searchTimeout);
    },

    searchMessages() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.$forceUpdate();
        if (this.searchQuery && this.filteredMessages.length > 0) {
          this.$nextTick(() => this.scrollToBottom());
        }
      }, 300);
    },

    goToProfile() {
      if (this.isGroupChat) {
        // Open group info sidebar for group chats
        this.groupInfoDrawer = true;
        return;
      }

      // Existing individual chat logic
      if (!this.otherUser) return;
      const userId = this.otherUser._id || this.otherUser.userID;
      if (!userId) return;

      if (this.otherUser.userType === "Agency") {
        this.$router.push({ name: "agency-profile-details", params: { id: userId } });
      } else if (this.otherUser.userType === "Volunteer") {
        this.$router.push({ name: "volunteer-profile-details", params: { id: userId } });
      }
    },
    handleLeftGroup(chatId) {
      // Handle leaving group
      console.log('Left group:', chatId);

      // Close the chat
      this.$store.commit('chat/setSingleRoom', null);

      // Remove the chat from the list
      this.$store.commit('chat/DELETE_CHAT_FOR_ME', chatId);

      // Navigate away
      this.$router.push({ name: 'chat-details', query: {} });

      // Show a message
      this.showSnackbar('You have left the group', 'info', 'mdi-information');
    },
    viewContact() {
      this.goToProfile();
    },

    startNewConversation() {
      this.$emit('start-conversation');
    },
    // In ChatMessages.vue - methods

    isSystemMessage(message) {
      if (!message) return false;

      // Check if it's a system message type
      if (message.messageType === "system") return true;

      // Check if it has systemMeta with action
      if (message.systemMeta?.action) return true;

      // Check for system message indicators in text
      if (message.text) {
        const systemIndicators = ['added', 'removed', 'joined', 'left', 'created', 'changed', 'updated', 'deleted'];
        const text = message.text.toLowerCase();
        if (systemIndicators.some(indicator => text.includes(indicator))) {
          return true;
        }
      }

      return false;
    },

    openImagePreview(imageSrc, message = null) {
      if (message) {
        const messageImages = this.getMessageImages(message);
        this.previewImages = [...messageImages];
        this.currentPreviewImageIndex = this.previewImages.indexOf(imageSrc);
        if (this.currentPreviewImageIndex === -1) {
          this.previewImages = [imageSrc];
          this.currentPreviewImageIndex = 0;
        }
      } else {
        this.previewImages = [imageSrc];
        this.currentPreviewImageIndex = 0;
      }
      this.previewImageSrc = imageSrc;
      this.imagePreviewDialog = true;
      this.$nextTick(() => {
        if (this.$refs.imageContainer) this.$refs.imageContainer.focus();
      });
    },

    closeImagePreview() {
      this.imagePreviewDialog = false;
      this.previewImages = [];
      this.currentPreviewImageIndex = 0;
    },

    getSenderAvatar(message) {
      if (!message || !message.sender) return require("@/assets/default.jpg");

      // Check if sender has image
      if (message.sender.image) {
        return message.sender.image;
      }

      // Check if sender has avatar
      if (message.sender.avatar) {
        return message.sender.avatar;
      }

      return require("@/assets/default.jpg");
    },

    openMessageMenu(message) {
      // Close any other open menus
      Object.keys(this.messageMenu).forEach(key => {
        if (key !== (message._id || message.clientMessageId)) {
          this.$set(this.messageMenu, key, false);
        }
      });

      // Toggle this menu
      const key = message._id || message.clientMessageId;
      this.$set(this.messageMenu, key, !this.messageMenu[key]);
    },

    getMenuPositionX(event) {
      if (event && event.clientX) {
        return event.clientX - 100;
      }
      return 0;
    },

    getMenuPositionY(event) {
      if (event && event.clientY) {
        return event.clientY - 50;
      }
      return 0;
    },

    // In the methods section
    getSenderName(message) {
      if (!message || !message.sender) return "Unknown";

      // If sender has a name property
      if (message.sender.name) return message.sender.name;

      // If sender has first_name and last_name
      if (message.sender.first_name) {
        return `${message.sender.first_name} ${message.sender.last_name || ''}`.trim();
      }

      // If sender has email, extract name
      if (message.sender.email) {
        return message.sender.email.split('@')[0];
      }

      // Check if this is the current user's message
      if (this.isOwnMessage(message)) return "You";

      // Try to find in participants
      if (this.singleRoom && this.singleRoom.participants) {
        const participant = this.singleRoom.participants.find(p => {
          const pId = p._id || p.userID || p.id;
          const senderId = message.sender._id || message.senderID || message.fromUser;
          return String(pId) === String(senderId);
        });
        if (participant) {
          return participant.name || participant.first_name || "User";
        }
      }

      return "User";
    },

    nextPreviewImage() {
      if (this.previewImages.length > 1 && this.currentPreviewImageIndex < this.previewImages.length - 1) {
        this.currentPreviewImageIndex++;
        this.previewImageSrc = this.previewImages[this.currentPreviewImageIndex];
      }
    },

    previousPreviewImage() {
      if (this.previewImages.length > 1 && this.currentPreviewImageIndex > 0) {
        this.currentPreviewImageIndex--;
        this.previewImageSrc = this.previewImages[this.currentPreviewImageIndex];
      }
    },

    goToPreviewImage(index) {
      if (this.previewImages && index >= 0 && index < this.previewImages.length) {
        this.currentPreviewImageIndex = index;
        this.previewImageSrc = this.previewImages[index];
      }
    },

    handlePreviewKeydown(event) {
      switch (event.key) {
        case "ArrowLeft": event.preventDefault(); this.previousPreviewImage(); break;
        case "ArrowRight": event.preventDefault(); this.nextPreviewImage(); break;
        case "Escape": event.preventDefault(); this.closeImagePreview(); break;
        case " ": event.preventDefault(); this.downloadImage(); break;
      }
    },

    handleImageClick(event) {
      event.stopPropagation();
      event.preventDefault();
    },

    downloadImage() {
      const link = document.createElement("a");
      link.href = this.currentPreviewImage;
      link.download = `image-${this.currentPreviewImageIndex + 1}-${new Date().getTime()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    onImageError(event) {
      console.error("Image failed to load:", event);
    },

    linkify(text) {
      if (!text) return "";
      return text.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
    },

    convertTZ(date, tzString) {
      if (!date) return new Date(new Date().toLocaleString("en-US", { timeZone: tzString || "America/New_York" }));
      return new Date((typeof date === "string" ? new Date(date) : date).toLocaleString("en-US", { timeZone: tzString || "America/New_York" }));
    },

    showSnackbar(message, color = "info", icon = "mdi-information") {
      this.snackMessage = message;
      this.snackbarColor = color;
      this.snackbarIcon = icon;
      this.snackbar = true;
    },

    // Check if a message contains a YouTube link
    isYouTubeLink(message) {
      if (!message || !message.text) return false;
      const text = message.text || message.message || '';
      const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([^\s&?]+)/i;
      return youtubeRegex.test(text);
    },

    // Extract YouTube video ID from message
    getYouTubeVideoId(message) {
      if (!message || !message.text) return null;
      const text = message.text || message.message || '';
      const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([^\s&?]+)/i;
      const match = text.match(youtubeRegex);
      return match ? match[1] : null;
    },

    // Get YouTube video thumbnail
    getYouTubeThumbnail(videoId) {
      if (!videoId) return '';
      // Using hqdefault for high quality thumbnail
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    },

    // Get YouTube embed URL
    getYouTubeEmbedUrl(videoId) {
      if (!videoId) return '';
      return `https://www.youtube.com/embed/${videoId}`;
    },

    // Get YouTube video URL
    getYouTubeUrl(videoId) {
      if (!videoId) return '';
      return `https://www.youtube.com/watch?v=${videoId}`;
    },

    // Check if message is a YouTube video
    hasYouTubeVideo(message) {
      return this.isYouTubeLink(message) && this.getYouTubeVideoId(message) !== null;
    },

    // Get YouTube video title (you'll need to fetch this from an API or parse from text)
    getYouTubeTitle(message) {
      if (!message) return 'YouTube Video';

      // If the message text contains more than just the URL, use that as title
      const text = message.text || message.message || '';
      const videoId = this.getYouTubeVideoId(message);
      if (!videoId) return 'YouTube Video';

      // Remove the YouTube URL from the text
      const title = text.replace(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)[^\s&?]+/i, '').trim();

      // If there's remaining text, use it as title
      if (title) return title;

      // Otherwise use a default title
      return 'YouTube Video';
    },

    // Format YouTube video duration (if available)
    formatYouTubeDuration(duration) {
      if (!duration) return '';
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },

    // Open YouTube video in a new window
    openYouTubeVideo(videoId) {
      if (videoId) {
        window.open(this.getYouTubeUrl(videoId), '_blank');
      }
    },

    // Open YouTube video in the app (optional - if you want to embed)
    playYouTubeVideo(videoId) {
      if (videoId) {
        // You can either open in new tab or show a dialog with embedded player
        // Option 1: Open in new tab
        window.open(this.getYouTubeUrl(videoId), '_blank');

        // Option 2: Show embedded player dialog (if you want)
        // this.showYouTubeDialog = true;
        // this.currentYouTubeVideoId = videoId;
      }
    },
    // Get text without the YouTube link
    getTextWithoutYouTubeLink(message) {
      if (!message) return '';
      const text = message.text || message.message || '';
      const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)[^\s&?]+/i;
      return text.replace(youtubeRegex, '').trim();
    },
    // Socket event handlers
    handleMessageDelivered(data) {
      if (data.roomId === this.singleRoom?._id) {
        const messageIndex = this.singleRoom.messages.findIndex(
          msg => msg._id === data.messageId || msg.clientMessageId === data.messageId
        );
        if (messageIndex !== -1) {
          this.$set(this.singleRoom.messages[messageIndex], 'delivered', true);
          this.$set(this.singleRoom.messages[messageIndex], 'status', 'delivered');
        }
      }
    },


    handleMessageStatusUpdate(data) {
      if (data.roomId === this.singleRoom?._id) {
        const messageIndex = this.singleRoom.messages.findIndex(
          msg => msg._id === data.messageId
        );
        if (messageIndex !== -1) {
          this.$set(this.singleRoom.messages[messageIndex], 'status', data.status);
          if (data.status === 'read') {
            this.$set(this.singleRoom.messages[messageIndex], 'read', true);
          } else if (data.status === 'delivered') {
            this.$set(this.singleRoom.messages[messageIndex], 'delivered', true);
          }
        }
      }
    }
  },

  mounted() {
    this.updateOtherUser();
    this.scrollToBottom();
    this.$refs.messagesContainer?.addEventListener("scroll", this.onScroll);
    document.addEventListener('click', this.handleClickOutside);

    // Setup socket event listeners - ONLY ONCE
    this.setupSocketListeners();

    // If there's a chat ID in the route, load it
    const chatId = this.$route.query.chatId;
    if (chatId) {
      this.$store.dispatch('chat/getChatById', chatId)
        .then(chat => {
          if (chat) {
            if (!chat.messages) chat.messages = [];
            this.$store.commit('chat/setSingleRoom', chat);
          }
        })
        .catch(() => this.$store.commit('chat/setSingleRoom', null));
    } else if (this.singleRoom?._id) {
      // If no route param but we have a singleRoom, join the room
      socket.joinChatRoom(this.singleRoom._id);
      socket.emit("join-room", this.singleRoom._id);
    }
  },

  beforeDestroy() {
    this.stopTyping();
    document.removeEventListener('click', this.handleClickOutside);
    // Clean up socket listeners
    this.cleanupSocketListeners();
    // Leave chat room
    if (this.singleRoom?._id) {
      socket.leaveChatRoom(this.singleRoom._id);
    }
    clearTimeout(this.typingTimeout);
    clearTimeout(this.userTypingTimeout);
    clearTimeout(this.searchTimeout);
    socket.off("typing-start", this.handleTypingStart);
    socket.off("typing-stop", this.handleTypingStop);
    socket.off("message-status-update", this.handleMessageStatusUpdate);
    socket.off("new-message");
    socket.off("message-delivered");
    socket.off("messages-read");
    socket.off("message-deleted");
    socket.off("message-deleted-for-everyone");
    socket.off("reaction-updated");
    socket.off("message-pinned");
    socket.off("room-joined");
    if (this.activeChat && this.activeChat._id) {
      socket.leaveChatRoom(this.activeChat._id);
    }
    this.$refs.messagesContainer?.removeEventListener("scroll", this.onScroll);
  }
};
</script>

<style scoped>
/* ===== MAIN CONTAINER ===== */
.chat-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  background: #e5ddd5;
  position: relative;
  overflow: hidden;
}

/* ===== CHAT HEADER - FIXED ===== */
.chat-header {
  background: linear-gradient(135deg, #084468, #3b7ca1);
  color: white;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 20;
  flex-shrink: 0;
  min-height: 64px;
  position: relative;
}

@media (max-width: 600px) {
  .chat-header {
    padding: 8px 12px;
    min-height: 56px;
  }
}

.header-content {
  display: flex;
  align-items: center;
  cursor: pointer;
  flex: 1;
  min-width: 0;
}

.back-button {
  margin-right: 12px;
  cursor: pointer;
  padding: 4px;
}

@media (max-width: 600px) {
  .back-button {
    margin-right: 8px;
  }
}

.user-avatar {
  position: relative;
  margin-right: 12px;
  flex-shrink: 0;
}

@media (max-width: 600px) {
  .user-avatar {
    margin-right: 8px;
  }
}

.online-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  background: #4caf50;
  border: 2px solid white;
  border-radius: 50%;
}

@media (max-width: 600px) {
  .online-indicator {
    width: 10px;
    height: 10px;
  }
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 600px) {
  .user-name {
    font-size: 14px;
  }
}

.user-status {
  font-size: 13px;
  opacity: 0.8;
}

@media (max-width: 600px) {
  .user-status {
    font-size: 12px;
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

/* ===== SEARCH BAR - MODERN ===== */
.search-bar {
  background: #ffffff;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
  z-index: 15;
  animation: slideDown 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-12px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.search-bar-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-input-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  background: #f0f2f5;
  border-radius: 12px;
  padding: 0 14px;
  transition: all 0.25s ease;
  border: 2px solid transparent;
  min-height: 44px;
  position: relative;
}

.search-input-wrapper:focus-within {
  background: #ffffff;
  border-color: #084468;
  box-shadow: 0 0 0 4px rgba(8, 68, 104, 0.08);
}

.search-icon {
  flex-shrink: 0;
  margin-right: 8px;
  color: #8696a0;
  transition: color 0.2s ease;
}

.search-input-wrapper:focus-within .search-icon {
  color: #084468;
}

.search-input-field {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  color: #111b21;
  padding: 10px 0;
  min-width: 0;
  font-family: inherit;
  letter-spacing: 0.3px;
}

.search-input-field::placeholder {
  color: #8696a0;
  font-weight: 400;
  letter-spacing: 0.2px;
}

.clear-search-btn {
  flex-shrink: 0;
  opacity: 0.6;
  transition: all 0.2s ease;
}

.clear-search-btn:hover {
  opacity: 1;
  transform: scale(1.1);
}

.clear-search-btn .v-icon {
  color: #8696a0;
}

.clear-search-btn:hover .v-icon {
  color: #54656f;
}

.search-close-btn {
  flex-shrink: 0;
  color: #667781 !important;
  transition: all 0.2s ease;
  width: 36px;
  height: 36px;
}

.search-close-btn:hover {
  background: rgba(0, 0, 0, 0.06) !important;
  color: #111b21 !important;
  transform: rotate(90deg);
}

.search-close-btn .v-icon {
  font-size: 22px;
}

.search-results-info {
  padding: 10px 16px 4px 16px;
  font-size: 0.875rem;
  color: #667781;
  display: flex;
  align-items: center;
  border-top: 1px solid rgba(0, 0, 0, 0.04);
  margin-top: 8px;
  padding-top: 10px;
}

.results-count {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #084468;
  font-size: 13px;
}

.results-count .v-icon {
  color: #084468;
}

.results-count.no-results {
  color: #8696a0;
  font-weight: 400;
}

.results-count.no-results .v-icon {
  color: #8696a0;
}

/* Dark mode for search */
.theme--dark .search-bar {
  background: #1a2a3a;
  border-bottom-color: rgba(255, 255, 255, 0.06);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.theme--dark .search-input-wrapper {
  background: #2a3942;
}

.theme--dark .search-input-wrapper:focus-within {
  background: #2a3942;
  border-color: #084468;
  box-shadow: 0 0 0 4px rgba(8, 68, 104, 0.15);
}

.theme--dark .search-input-field {
  color: #e9edef;
}

.theme--dark .search-input-field::placeholder {
  color: #8696a0;
}

.theme--dark .search-icon {
  color: #8696a0;
}

.theme--dark .search-input-wrapper:focus-within .search-icon {
  color: #4FC3F7;
}

.theme--dark .search-close-btn {
  color: #8696a0 !important;
}

.theme--dark .search-close-btn:hover {
  background: rgba(255, 255, 255, 0.06) !important;
  color: #e9edef !important;
}

.theme--dark .clear-search-btn .v-icon {
  color: #8696a0;
}

.theme--dark .clear-search-btn:hover .v-icon {
  color: #e9edef;
}

.theme--dark .search-results-info {
  border-top-color: rgba(255, 255, 255, 0.06);
}

.theme--dark .results-count {
  color: #4FC3F7;
}

.theme--dark .results-count .v-icon {
  color: #4FC3F7;
}

.theme--dark .results-count.no-results {
  color: #8696a0;
}

.theme--dark .results-count.no-results .v-icon {
  color: #8696a0;
}

/* Mobile responsive for search */
@media (max-width: 600px) {
  .search-bar {
    padding: 10px 12px;
  }

  .search-bar-content {
    gap: 8px;
  }

  .search-input-wrapper {
    min-height: 40px;
    border-radius: 10px;
    padding: 0 12px;
  }

  .search-input-field {
    font-size: 14px;
    padding: 8px 0;
  }

  .search-close-btn {
    width: 32px;
    height: 32px;
  }

  .search-close-btn .v-icon {
    font-size: 18px;
  }

  .search-icon {
    font-size: 18px !important;
  }

  .search-results-info {
    font-size: 13px;
    padding: 8px 12px 2px 12px;
  }

  .results-count {
    font-size: 12px;
  }
}

/* Very small screens */
@media (max-width: 380px) {
  .search-bar {
    padding: 8px 10px;
  }

  .search-input-wrapper {
    min-height: 36px;
    border-radius: 8px;
    padding: 0 10px;
  }

  .search-input-field {
    font-size: 13px;
    padding: 6px 0;
  }

  .search-close-btn {
    width: 28px;
    height: 28px;
  }

  .search-close-btn .v-icon {
    font-size: 16px;
  }
}

/* Search results animation */
.search-results-info {
  animation: fadeInUp 0.3s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== MESSAGES CONTAINER - SCROLLABLE ===== */
.messages-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  min-height: 0;
  background: #e5ddd5;
}

.messages-container::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: url("../../assets/chat-bg.jpg");
  opacity: 0.1;
  z-index: -1;
  pointer-events: none;
}

.messages-wrapper {
  padding: 16px;
  min-height: 100%;
}

@media (max-width: 600px) {
  .messages-wrapper {
    padding: 12px;
  }
}

/* ===== TYPING INDICATOR ===== */
.fixed-typing-indicator {
  flex-shrink: 0;
  padding: 4px 16px 8px 16px;
  background: transparent;
  z-index: 10;
  min-height: 36px;
}

@media (max-width: 600px) {
  .fixed-typing-indicator {
    padding: 2px 12px 6px 12px;
    min-height: 30px;
  }
}

.typing-content {
  display: inline-flex;
  align-items: center;
  background: rgba(240, 242, 245, 0.95);
  border-radius: 18px;
  padding: 4px 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.typing-dots {
  display: inline-flex;
  align-items: center;
  margin-right: 8px;
  height: 20px;
}

.typing-dots .dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #65676b;
  margin: 0 2px;
  animation: typingPulse 1.4s infinite both;
}

.typing-dots .dot:nth-child(1) {
  animation-delay: 0ms;
}

.typing-dots .dot:nth-child(2) {
  animation-delay: 160ms;
}

.typing-dots .dot:nth-child(3) {
  animation-delay: 320ms;
}

@keyframes typingPulse {

  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.6;
  }

  40% {
    transform: translateY(-3px);
    opacity: 1;
  }
}

.typing-text {
  font-size: 14px;
  color: #65676b;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 600px) {
  .typing-text {
    font-size: 12px;
  }
}

/* ===== MESSAGE INPUT ===== */
.message-input-section {
  background: white;
  border-top: 1px solid #e0e0e0;
  padding: 8px 12px;
  flex-shrink: 0;
  z-index: 10;
}

@media (max-width: 600px) {
  .message-input-section {
    padding: 6px 8px;
  }
}

.input-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

@media (max-width: 600px) {
  .input-row {
    gap: 4px;
  }
}

.text-input-container {
  flex: 1;
  display: flex;
  align-items: flex-end;
  background: white;
  border-radius: 24px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  min-width: 0;
}

@media (max-width: 600px) {
  .text-input-container {
    border-radius: 20px;
  }
}

.attachment-btn {
  min-width: 40px;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  margin-bottom: 4px;
}

@media (max-width: 600px) {
  .attachment-btn {
    min-width: 36px;
    width: 36px;
    height: 36px;
    margin-bottom: 0;
  }

  .attachment-btn .v-icon {
    font-size: 18px !important;
  }
}

.message-input {
  flex: 1;
  min-width: 0;
}

.message-input>>>.v-input__control {
  min-height: auto !important;
}

.message-input>>>.v-text-field__details {
  display: none;
}

.message-input>>>fieldset {
  border: none;
}

.message-input>>>textarea {
  padding: 10px 16px !important;
  max-height: 120px;
  line-height: 1.4;
  font-size: 14px;
}

@media (max-width: 600px) {
  .message-input>>>textarea {
    padding: 8px 12px !important;
    max-height: 80px;
    font-size: 15px;
  }
}

.emoji-btn {
  min-width: 36px;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
}

@media (max-width: 600px) {
  .emoji-btn {
    min-width: 32px;
    width: 32px;
    height: 32px;
  }

  .emoji-btn .v-icon {
    font-size: 18px !important;
  }
}

.send-btn {
  min-width: 40px;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  margin-bottom: 4px;
}

@media (max-width: 600px) {
  .send-btn {
    min-width: 36px;
    width: 36px;
    height: 36px;
    margin-bottom: 0;
  }

  .send-btn .v-icon {
    font-size: 18px !important;
  }
}

/* ===== ATTACHMENT PREVIEW ===== */
.attachment-preview {
  padding: 8px 12px;
  border-bottom: 1px solid #e0e0e0;
  background: #f9f9f9;
  margin-bottom: 6px;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.preview-count {
  font-size: 12px;
  font-weight: 500;
  color: #666;
}

.clear-all-btn {
  background: rgba(0, 0, 0, 0.05) !important;
}

.preview-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.preview-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  background: white;
}

.preview-image {
  border-radius: 8px;
}

.remove-single-btn {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #f44336 !important;
  width: 20px !important;
  height: 20px !important;
}

.add-more-container {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 8px;
  text-align: center;
}

.add-more-btn {
  font-size: 12px !important;
}

/* ===== MESSAGE STYLES ===== */
.empty-messages {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px;
}

.date-group {
  margin-bottom: 20px;
}

.date-separator {
  text-align: center;
  margin: 16px 0;
}

.date-text {
  background: rgba(0, 0, 0, 0.1);
  color: #666;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.message-wrapper {
  display: flex;
  margin-bottom: 4px;
}

.message-own {
  justify-content: flex-end;
}

.message-other {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 65%;
  padding: 8px 12px;
  border-radius: 18px;
  position: relative;
  word-wrap: break-word;
  box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.1);
}

@media (max-width: 600px) {
  .message-bubble {
    max-width: 85%;
  }
}

.bubble-own {
  background: #dcf8c6;
  border-bottom-right-radius: 4px;
  margin-left: auto;
}

.bubble-other {
  background: white;
  border-bottom-left-radius: 4px;
}

.message-content-container {
  position: relative;
  padding-right: 24px;
}

/* ===== MESSAGE ACTIONS ===== */
.message-actions {
  position: absolute;
  top: -4px;
  right: -8px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 5;
}

.message-bubble:hover .message-actions {
  opacity: 1;
}

@media (hover: none) {
  .message-actions {
    opacity: 1;
  }
}

@media (max-width: 600px) {
  .message-actions {
    opacity: 1;
    top: -2px;
    right: -6px;
  }

  .action-btn {
    background-color: rgba(0, 0, 0, 0.08) !important;
    width: 28px !important;
    height: 28px !important;
    min-width: 28px !important;
  }

  .action-btn .v-icon {
    font-size: 16px !important;
  }

  .message-content-container {
    padding-right: 32px !important;
  }
}

.action-btn {
  background-color: rgba(0, 0, 0, 0.05) !important;
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50% !important;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background-color: rgba(0, 0, 0, 0.12) !important;
  transform: scale(1.1);
}

.action-btn:active {
  transform: scale(0.9);
}

.action-menu {
  min-width: 180px;
  border-radius: 8px !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15) !important;
  overflow: hidden;
  padding: 4px 0 !important;
}

.action-item {
  font-size: 14px !important;
  font-weight: 400;
}

.action-menu .v-list-item {
  min-height: 40px !important;
  padding: 0 16px !important;
}

.action-menu .v-list-item:hover {
  background: rgba(0, 0, 0, 0.04) !important;
}

.action-menu .v-list-item .v-list-item-icon {
  margin-right: 12px !important;
  min-width: 24px !important;
}

/* Dark mode for actions */
.theme--dark .action-btn {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .action-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.theme--dark .action-menu {
  background: #1a2a3a !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3) !important;
}

.theme--dark .action-menu .v-list-item:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

.theme--dark .action-item {
  color: #e9edef !important;
}

/* Mobile menu overlay */
@media (max-width: 600px) {
  .message-action-menu {
    position: fixed !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
    width: 90% !important;
    max-width: 320px !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2) !important;
  }

  .message-action-menu .v-list {
    border-radius: 12px !important;
    padding: 8px 0 !important;
  }

  .message-action-menu .v-list-item {
    padding: 12px 16px !important;
    min-height: 48px !important;
  }

  .message-action-menu .v-list-item .v-icon {
    font-size: 20px !important;
  }

  .message-action-menu .v-list-item-title {
    font-size: 15px !important;
  }

  .message-action-menu::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: -1;
  }

  .theme--dark .message-action-menu {
    background: #1a2a3a !important;
  }

  .theme--dark .message-action-menu .v-list {
    background: #1a2a3a !important;
  }

  .theme--dark .message-action-menu .v-list-item {
    color: #e9edef !important;
  }
}

/* ===== MESSAGE IMAGE ===== */
.message-image {
  margin-bottom: 6px;
}

.message-image-grid {
  display: grid;
  gap: 4px;
  border-radius: 12px;
  overflow: hidden;
  max-width: 300px;
}

.grid-single {
  grid-template-columns: 1fr;
}

.grid-two {
  grid-template-columns: 1fr 1fr;
}

.grid-three {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
}

.grid-three .message-img:first-child {
  grid-row: 1 / 3;
}

.grid-multiple {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  position: relative;
}

.img-large {
  max-height: 300px;
}

.img-medium {
  max-height: 150px;
}

.img-small {
  max-height: 100px;
}

.img-hidden {
  display: none;
}

.message-img {
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}

.message-img:hover {
  transform: scale(1.02);
}

.image-count-overlay {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

/* ===== MESSAGE TEXT ===== */
.message-text {
  font-size: 14px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 600px) {
  .message-text {
    font-size: 15px;
  }
}

.message-edited {
  font-size: 10px;
  color: #666;
  font-style: italic;
  margin-top: 2px;
}

.message-meta {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 4px;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  gap: 4px;
}

.message-time {
  margin-right: 4px;
}

.status-icon {
  margin-left: 2px;
}

.failed-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.failed-indicator .retry-text {
  font-size: 10px;
  color: #e74c3c;
}

/* ===== SCROLL BOTTOM BUTTON ===== */
.scroll-bottom-btn {
  position: sticky;
  bottom: 20px;
  right: 20px;
  float: right;
  z-index: 5;
  margin-top: -50px;
}

@media (max-width: 600px) {
  .scroll-bottom-btn {
    bottom: 15px;
    right: 10px;
  }
}

/* ===== IMAGE PREVIEW ===== */
.image-preview-wrapper {
  background-color: rgba(0, 0, 0, 0.9);
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  touch-action: none;
}

.preview-toolbar {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 100%);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
}

@media (max-width: 600px) {
  .preview-toolbar {
    padding: 12px 16px;
  }
}

.spacer {
  flex-grow: 1;
}

.close-btn,
.action-btn {
  margin: 0 4px;
}

.preview-counter {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 8px 16px;
  border-radius: 20px;
  margin: 0 16px;
}

.counter-text {
  color: white;
  font-weight: 500;
  font-size: 14px;
}

.image-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 80px 20px 20px;
  touch-action: none;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  cursor: default;
  user-select: none;
}

.image-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}

.navigation-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 50;
}

.nav-overlay-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.6) !important;
  backdrop-filter: blur(10px);
  pointer-events: all;
  transition: all 0.3s ease;
}

.nav-overlay-btn:hover {
  background: rgba(0, 0, 0, 0.8) !important;
  transform: translateY(-50%) scale(1.1);
}

.nav-left {
  left: 20px;
}

.nav-right {
  right: 20px;
}

@media (max-width: 600px) {
  .nav-left {
    left: 10px;
  }

  .nav-right {
    right: 10px;
  }
}

.preview-thumbnails {
  position: fixed;
  bottom: 80px;
  left: 0;
  right: 0;
  z-index: 10;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%);
  padding: 20px;
}

.thumbnails-container {
  display: flex;
  justify-content: center;
  gap: 12px;
  overflow-x: auto;
  padding: 8px;
  max-width: 100%;
}

.thumbnail-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  flex-shrink: 0;
}

.thumbnail-item:hover {
  transform: scale(1.1);
  border-color: rgba(255, 255, 255, 0.5);
}

.thumbnail-item.active {
  border-color: #fff;
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
}

.thumbnail-img {
  border-radius: 6px;
}

.thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}

.keyboard-hints {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  padding: 8px 16px;
  border-radius: 20px;
  z-index: 10;
}

.hint-item {
  color: white;
  font-size: 11px;
  opacity: 0.8;
}

@media (max-width: 600px) {
  .image-container {
    padding: 70px 10px 10px;
  }

  .preview-thumbnails {
    padding: 12px;
    bottom: 60px;
  }

  .thumbnail-item {
    width: 40px;
    height: 40px;
  }

  .thumbnails-container {
    gap: 8px;
  }

  .keyboard-hints {
    bottom: 10px;
    gap: 8px;
    padding: 6px 12px;
  }

  .hint-item {
    font-size: 10px;
  }
}

/* ===== REACTIONS ===== */
.message-reactions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
  align-items: center;
}

.message-reactions.empty-reactions {
  margin-top: 0;
  opacity: 0;
  transition: opacity 0.2s ease;
  height: 24px;
  min-height: 24px;
}

.message-bubble:hover .message-reactions.empty-reactions {
  opacity: 1;
}

.reaction-pill {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 10px 3px 8px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  user-select: none;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  min-height: 28px;
}

.reaction-pill:hover {
  background: rgba(0, 0, 0, 0.08);
  transform: scale(1.05);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.reaction-pill.reaction-self {
  background: rgba(18, 140, 126, 0.15);
  border-color: #128C7E;
  box-shadow: 0 1px 3px rgba(18, 140, 126, 0.2);
}

.reaction-pill.reaction-self:hover {
  background: rgba(18, 140, 126, 0.25);
}

.reaction-pill.add-reaction-pill {
  background: rgba(0, 0, 0, 0.03);
  border: 1px dashed rgba(0, 0, 0, 0.15);
  padding: 3px 8px;
  min-width: 28px;
  justify-content: center;
  min-height: 28px;
}

.reaction-pill.add-reaction-pill:hover {
  background: rgba(0, 0, 0, 0.08);
  transform: scale(1.05);
  border-color: rgba(0, 0, 0, 0.25);
}

.reaction-pill.add-reaction-pill .v-icon {
  transition: all 0.3s ease;
}

.reaction-pill.add-reaction-pill:hover .v-icon {
  transform: rotate(90deg);
}

.reaction-emoji {
  font-size: 16px;
  line-height: 1.2;
}

.reaction-count {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  min-width: 14px;
  text-align: center;
}

.reaction-pill.reaction-self .reaction-count {
  color: #128C7E;
}

/* Mobile Reaction Styles */
@media (max-width: 600px) {
  .message-reactions {
    gap: 6px;
    margin-top: 6px;
  }

  .message-reactions.empty-reactions {
    height: 32px;
    min-height: 32px;
    opacity: 1;
  }

  .reaction-pill {
    padding: 4px 12px 4px 10px;
    font-size: 16px;
    min-height: 32px;
    border-radius: 20px;
  }

  .reaction-pill.add-reaction-pill {
    padding: 4px 10px;
    min-width: 32px;
    min-height: 32px;
  }

  .reaction-emoji {
    font-size: 18px;
  }

  .reaction-count {
    font-size: 13px;
    min-width: 16px;
  }

  .add-reaction-pill-mobile {
    background: rgba(0, 0, 0, 0.05) !important;
    border: 1px solid rgba(0, 0, 0, 0.08) !important;
  }

  .add-reaction-pill-mobile .v-icon {
    color: #666 !important;
  }

  .theme--dark .add-reaction-pill-mobile {
    background: rgba(255, 255, 255, 0.08) !important;
    border-color: rgba(255, 255, 255, 0.1) !important;
  }

  .theme--dark .add-reaction-pill-mobile .v-icon {
    color: #8696a0 !important;
  }

  /* Mobile Reaction Picker - Bottom Sheet */
  .reaction-picker-mobile {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    top: auto !important;
    transform: none !important;
    width: 100% !important;
    max-width: 100% !important;
    border-radius: 20px 20px 0 0 !important;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15) !important;
    animation: slideUp 0.3s ease !important;
    background: transparent !important;
    max-height: 60vh !important;
    overflow-y: auto !important;
  }

  .reaction-picker-mobile .reaction-picker-card {
    border-radius: 20px 20px 0 0 !important;
    padding: 16px 12px 20px !important;
    background: #ffffff !important;
    box-shadow: none !important;
  }

  .reaction-picker-mobile .reaction-grid {
    grid-template-columns: repeat(6, 1fr);
    gap: 8px;
    max-width: 100%;
    padding: 4px;
  }

  .reaction-picker-mobile .reaction-emoji-option {
    font-size: 36px !important;
    padding: 8px !important;
    width: 52px !important;
    height: 52px !important;
    border-radius: 14px !important;
  }

  .reaction-picker-mobile .reaction-emoji-option.more-option {
    font-size: 28px !important;
  }

  .reaction-picker-mobile::before {
    content: '';
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 4px;
    background: rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    z-index: 10;
  }

  .reaction-picker-mobile {
    background: rgba(0, 0, 0, 0.3) !important;
  }

  .theme--dark .reaction-picker-mobile {
    background: rgba(0, 0, 0, 0.5) !important;
  }

  .theme--dark .reaction-picker-mobile .reaction-picker-card {
    background: #1a2a3a !important;
  }

  .theme--dark .reaction-picker-mobile::before {
    background: rgba(255, 255, 255, 0.2);
  }

  .theme--dark .reaction-picker-mobile .reaction-emoji-option:hover {
    background: rgba(255, 255, 255, 0.08) !important;
  }

  .theme--dark .reaction-picker-mobile .reaction-emoji-option.reaction-selected {
    background: rgba(18, 140, 126, 0.3) !important;
    border-color: #128C7E !important;
  }
}

/* Desktop Reaction Picker */
.reaction-picker-desktop .reaction-picker-card {
  padding: 12px 10px;
  border-radius: 16px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
  max-width: 280px;
  background: white;
}

.reaction-picker-desktop .reaction-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 4px;
  max-width: 260px;
}

.reaction-picker-desktop .reaction-emoji-option {
  font-size: 28px;
  cursor: pointer;
  padding: 4px;
  text-align: center;
  border-radius: 10px;
  transition: all 0.15s ease;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reaction-picker-desktop .reaction-emoji-option:hover {
  background: rgba(0, 0, 0, 0.06);
  transform: scale(1.2);
}

.reaction-picker-desktop .reaction-emoji-option.reaction-selected {
  background: rgba(18, 140, 126, 0.15);
  border: 2px solid #128C7E;
  border-radius: 10px;
}

.reaction-picker-desktop .reaction-emoji-option.more-option {
  font-size: 24px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reaction-picker-desktop .reaction-emoji-option.more-option:hover {
  background: rgba(0, 0, 0, 0.08);
  transform: scale(1.1);
}

/* Dark mode desktop */
.theme--dark .reaction-picker-desktop .reaction-picker-card {
  background: #1a2a3a !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3) !important;
}

.theme--dark .reaction-picker-desktop .reaction-emoji-option:hover {
  background: rgba(255, 255, 255, 0.08);
}

.theme--dark .reaction-picker-desktop .reaction-emoji-option.reaction-selected {
  background: rgba(18, 140, 126, 0.3);
  border-color: #128C7E;
}

.theme--dark .reaction-picker-desktop .reaction-emoji-option.more-option {
  background: rgba(255, 255, 255, 0.05);
}

.theme--dark .reaction-picker-desktop .reaction-emoji-option.more-option:hover {
  background: rgba(255, 255, 255, 0.1);
}

.reaction-pill[title] {
  position: relative;
}

.reaction-pill[title]:hover::after {
  content: attr(title);
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  white-space: nowrap;
  z-index: 100;
  pointer-events: none;
  font-weight: 400;
}

@media (max-width: 600px) {
  .reaction-pill[title]:hover::after {
    display: none;
  }
}

.reaction-loading {
  opacity: 0.5;
  pointer-events: none;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
}

@media (max-width: 480px) {
  .reaction-picker-mobile .reaction-grid {
    grid-template-columns: repeat(6, 1fr);
    gap: 6px;
  }

  .reaction-picker-mobile .reaction-emoji-option {
    font-size: 30px !important;
    padding: 6px !important;
    width: 44px !important;
    height: 44px !important;
  }

  .reaction-picker-mobile .reaction-emoji-option.more-option {
    font-size: 24px !important;
  }
}

/* ===== PINNED MESSAGES BANNER ===== */
.pinned-messages-banner {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 8px 12px;
  margin-bottom: 16px;
  border: 1px solid #e8eaed;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  position: sticky;
  top: 0;
  z-index: 5;
  backdrop-filter: blur(10px);
  background: rgba(248, 249, 250, 0.95);
}

.pinned-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e8eaed;
  margin-bottom: 6px;
}

.pinned-title {
  font-size: 13px;
  font-weight: 600;
  color: #3c4043;
}

.pinned-count {
  font-size: 12px;
  color: #80868b;
}

.pinned-nav-btn {
  color: #80868b !important;
}

.pinned-nav-btn:hover {
  background: rgba(0, 0, 0, 0.05) !important;
}

.pinned-messages-list {
  max-height: 150px;
  overflow-y: auto;
}

.pinned-messages-list::-webkit-scrollbar {
  width: 3px;
}

.pinned-messages-list::-webkit-scrollbar-track {
  background: transparent;
}

.pinned-messages-list::-webkit-scrollbar-thumb {
  background: #dadce0;
  border-radius: 4px;
}

.pinned-message-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.pinned-message-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.pinned-message-item:active {
  background: rgba(0, 0, 0, 0.08);
}

.pinned-message-content {
  flex: 1;
  min-width: 0;
}

.pinned-message-sender {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
}

.pinned-sender-name {
  font-weight: 600;
  color: #084468;
}

.pinned-message-time {
  color: #80868b;
  font-size: 10px;
}

.pinned-message-text {
  font-size: 13px;
  color: #3c4043;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.unpin-btn {
  opacity: 0.4;
  transition: opacity 0.2s ease;
  padding: 2px !important;
}

.unpin-btn:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.05) !important;
}

.pinned-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(245, 124, 0, 0.08);
  padding: 2px 8px;
  border-radius: 12px;
  margin-bottom: 4px;
  font-size: 10px;
  color: #f57c00;
}

.pinned-badge-text {
  font-weight: 500;
}

.highlight-pinned {
  animation: highlightPulse 2s ease;
}

@keyframes highlightPulse {
  0% {
    background: rgba(245, 124, 0, 0.2);
    transform: scale(1.02);
  }

  50% {
    background: rgba(245, 124, 0, 0.05);
    transform: scale(1);
  }

  100% {
    background: transparent;
    transform: scale(1);
  }
}

/* ===== SYSTEM MESSAGES ===== */
.message-wrapper.message-system {
  justify-content: center !important;
  margin: 8px 0;
}

.system-message {
  background: rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  padding: 6px 16px;
  max-width: 85%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  text-align: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

@media (max-width: 600px) {
  .system-message {
    max-width: 92%;
    padding: 5px 12px;
  }
}

.system-message-text {
  font-size: 13px;
  color: #65676b;
  font-weight: 400;
  line-height: 1.4;
  word-break: break-word;
}

@media (max-width: 600px) {
  .system-message-text {
    font-size: 12px;
  }
}

.system-message-time {
  font-size: 10px;
  color: rgba(0, 0, 0, 0.35);
  margin-top: 1px;
}

/* ===== SENDER AVATAR ===== */
.message-sender-avatar {
  position: absolute;
  bottom: -8px;
  left: -18px;
  z-index: 1;
}

.sender-avatar {
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.message-other .message-bubble {
  position: relative;
  margin-left: 24px;
}

.message-other .message-sender-avatar {
  left: -16px;
  bottom: -6px;
}

.message-sender-name {
  font-size: 12px;
  font-weight: 600;
  color: #084468;
  margin-bottom: 2px;
  margin-left: 4px;
}

/* ===== REPLY BAR ===== */
.reply-bar {
  flex-shrink: 0;
  padding: 6px 16px;
  background: #f0f2f5;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
}

.reply-bar-content {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.reply-info {
  display: flex;
  align-items: center;
  gap: 6px;
}

.reply-label {
  font-size: 12px;
  font-weight: 500;
  color: #128C7E;
}

.reply-preview-text {
  flex: 1;
  font-size: 13px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cancel-reply-btn {
  color: #666 !important;
}

.reply-preview {
  background: rgba(0, 0, 0, 0.05);
  border-left: 3px solid #128C7E;
  padding: 4px 8px;
  margin-bottom: 6px;
  border-radius: 4px;
}

.reply-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 2px;
}

.reply-sender {
  font-size: 11px;
  font-weight: 500;
  color: #128C7E;
}

.reply-content {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ===== DARK MODE OVERRIDES ===== */
.theme--dark .chat-container {
  background: #0b141a;
}

.theme--dark .chat-header {
  background: linear-gradient(135deg, #0a1a2b, #1a3a5a);
}

.theme--dark .messages-container {
  background: #0b141a;
}

.theme--dark .bubble-other {
  background: #1a2a3a;
  color: #e9edef;
}

.theme--dark .bubble-own {
  background: #0a4a3a;
  color: #e9edef;
}

.theme--dark .date-text {
  background: rgba(255, 255, 255, 0.1);
  color: #8696a0;
}

.theme--dark .message-input-section {
  background: #1a2a3a;
  border-top-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .text-input-container {
  background: #2a3a4a;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .message-input>>>textarea {
  color: #e9edef;
}

.theme--dark .attachment-preview {
  background: #1a2a3a;
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .preview-item {
  background: #2a3a4a;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .add-more-container {
  border-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .typing-content {
  background: rgba(26, 42, 58, 0.95);
  border-color: rgba(255, 255, 255, 0.05);
}

.theme--dark .typing-text {
  color: #8696a0;
}

.theme--dark .reply-bar {
  background: #1a2a3a;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .reply-preview-text {
  color: #8696a0;
}

.theme--dark .reply-preview {
  background: rgba(255, 255, 255, 0.05);
}

.theme--dark .reply-content {
  color: #8696a0;
}

.theme--dark .system-message {
  background: rgba(255, 255, 255, 0.08);
}

.theme--dark .system-message-text {
  color: #8696a0;
}

.theme--dark .system-message-time {
  color: rgba(255, 255, 255, 0.3);
}

.theme--dark .sender-avatar {
  border-color: #1a2a3a;
}

.theme--dark .message-sender-name {
  color: #4FC3F7;
}

/* ===== DIALOG STYLES ===== */
.rounded-xl {
  border-radius: 16px !important;
}

.rounded-pill {
  border-radius: 50px !important;
}

.rounded-lg {
  border-radius: 8px !important;
}

/* ===== EMOJI PICKER DIALOG ===== */
.emoji-picker-dialog {
  overflow: hidden;
  max-height: 90vh;
}

.emoji-picker-dialog .dialog-header {
  background: linear-gradient(135deg, #084468, #3b7ca1);
  color: white;
  padding: 16px 24px;
}

.emoji-picker-wrapper {
  max-height: 400px;
  overflow-y: auto;
  min-height: 300px;
}

.emoji-picker-wrapper>>>.emoji-picker {
  background: transparent !important;
  border: none !important;
}

.emoji-picker-wrapper>>>.emoji-picker .emojis-search {
  border-radius: 20px !important;
  background: #f0f2f5 !important;
  border: 2px solid transparent !important;
  padding: 8px 16px !important;
  font-size: 14px !important;
  width: 100% !important;
  outline: none !important;
  margin-bottom: 12px !important;
}

.emoji-picker-wrapper>>>.emoji-picker .emojis-search:focus {
  border-color: #084468 !important;
  background: white !important;
  box-shadow: 0 0 0 4px rgba(8, 68, 104, 0.1) !important;
}

.emoji-picker-wrapper>>>.emoji-picker .emojis-list {
  padding: 4px !important;
}

.emoji-picker-wrapper>>>.emoji-picker .emoji {
  font-size: 28px !important;
  padding: 4px !important;
  border-radius: 8px !important;
  transition: all 0.15s ease !important;
  cursor: pointer !important;
}

.emoji-picker-wrapper>>>.emoji-picker .emoji:hover {
  background: rgba(0, 0, 0, 0.06) !important;
  transform: scale(1.15) !important;
}

.emoji-picker-wrapper>>>.emoji-picker .emoji:active {
  transform: scale(0.9) !important;
}

.emoji-picker-wrapper>>>.emoji-picker .categories {
  padding: 4px 0 !important;
}

.emoji-picker-wrapper>>>.emoji-picker .category {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #41525d !important;
  padding: 8px 4px !important;
}

.current-reaction-status {
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.status-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #555;
}

.current-emoji {
  font-size: 24px;
  line-height: 1;
}

.remove-link {
  color: #e74c3c;
  cursor: pointer;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-size: 13px;
  background: rgba(231, 76, 60, 0.08);
}

.remove-link:hover {
  background: rgba(231, 76, 60, 0.15);
  transform: scale(1.05);
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .chat-container.mobile-chat .chat-header {
    padding: 8px 12px;
  }

  .chat-container.mobile-chat .messages-wrapper {
    padding: 8px;
  }

  .chat-container.mobile-chat .message-bubble {
    max-width: 90%;
  }
}

@media (max-width: 600px) {
  .reaction-grid {
    grid-template-columns: repeat(6, 1fr);
    gap: 1px;
    max-width: 210px;
  }

  .reaction-emoji-option {
    font-size: 24px;
    width: 32px;
    height: 32px;
    padding: 2px;
  }

  .reaction-pill {
    padding: 1px 6px 1px 4px;
    font-size: 12px;
  }

  .reaction-emoji {
    font-size: 13px;
  }

  .reaction-count {
    font-size: 10px;
    min-width: 12px;
  }

  .add-reaction-btn {
    width: 18px;
    height: 18px;
    min-width: 18px;
    min-height: 18px;
  }

  .add-reaction-btn .v-icon {
    font-size: 12px !important;
  }

  .message-reactions.empty-reactions {
    opacity: 1;
  }

  .emoji-picker-wrapper {
    max-height: 300px;
    min-height: 200px;
  }

  .emoji-picker-wrapper>>>.emoji-picker .emoji {
    font-size: 24px !important;
    padding: 2px !important;
  }

  .current-reaction-status {
    padding: 8px 12px;
  }

  .reply-bar {
    padding: 4px 12px;
  }

  .reply-preview-text {
    font-size: 12px;
  }

  .pinned-messages-banner {
    padding: 6px 10px;
    margin-bottom: 12px;
    border-radius: 8px;
  }

  .pinned-messages-list {
    max-height: 120px;
  }

  .pinned-message-text {
    font-size: 12px;
  }

  .pinned-title {
    font-size: 12px;
  }

  .pinned-message-sender {
    font-size: 10px;
  }

  .pinned-sender-name {
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .chat-container.mobile-chat .chat-header {
    min-height: 48px;
  }

  .chat-container.mobile-chat .user-name {
    font-size: 13px;
  }

  .chat-container.mobile-chat .user-status {
    font-size: 11px;
  }

  .chat-container.mobile-chat .message-bubble {
    padding: 6px 10px;
  }

  .chat-container.mobile-chat .message-text {
    font-size: 14px;
  }

  .chat-container.mobile-chat .message-input>>>textarea {
    font-size: 14px;
    padding: 6px 10px !important;
  }

  .chat-container.mobile-chat .input-row {
    gap: 2px;
  }

  .chat-container.mobile-chat .attachment-btn,
  .chat-container.mobile-chat .send-btn {
    min-width: 32px;
    width: 32px;
    height: 32px;
  }

  .chat-container.mobile-chat .emoji-btn {
    min-width: 28px;
    width: 28px;
    height: 28px;
  }
}

/* Add to the style section */
.message-sender-name {
  font-size: 12px;
  font-weight: 600;
  color: #084468;
  margin-bottom: 2px;
  margin-left: 4px;
}

/* For own messages in group chat, show "You" */
.message-own .message-sender-name {
  color: #128C7E;
  text-align: right;
  margin-right: 4px;
}

/* Dark mode */
.theme--dark .message-sender-name {
  color: #4FC3F7;
}

.theme--dark .message-own .message-sender-name {
  color: #81C784;
}

/* Add or update these styles */
.message-other .message-bubble {
  position: relative;
  margin-left: 24px;
}

.message-other .message-sender-avatar {
  position: absolute;
  bottom: -6px;
  left: -16px;
  z-index: 1;
}

.message-own .message-bubble {
  margin-right: 0;
}

/* For own messages, show "You" label on the right */
.message-own .message-sender-name {
  text-align: right;
  padding-right: 4px;
}

.message-group .message-bubble {
  margin-bottom: 4px;
}

.message-group .message-other .message-bubble {
  margin-left: 32px;
}

.message-group .message-own .message-bubble {
  margin-right: 8px;
}

/* ===== FILE ATTACHMENTS ===== */
.message-files {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 6px;
  max-width: 100%;
}

.file-attachment {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.06);
  max-width: 280px;
}

.file-attachment:hover {
  background: rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.file-attachment:active {
  transform: scale(0.98);
}

.file-icon-wrapper {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(33, 150, 243, 0.1);
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-size: 13px;
  font-weight: 500;
  color: #111b21;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-meta {
  display: flex;
  gap: 8px;
  font-size: 11px;
  color: #8696a0;
  margin-top: 2px;
}

.file-size {
  font-weight: 500;
}

.file-type {
  text-transform: capitalize;
}

.download-file-btn {
  flex-shrink: 0;
  opacity: 0.4;
  transition: opacity 0.2s ease;
}

.file-attachment:hover .download-file-btn {
  opacity: 1;
}

.download-file-btn:hover {
  background: rgba(0, 0, 0, 0.06) !important;
}

/* Dark mode for files */
.theme--dark .file-attachment {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.06);
}

.theme--dark .file-attachment:hover {
  background: rgba(255, 255, 255, 0.08);
}

.theme--dark .file-name {
  color: #e9edef;
}

.theme--dark .file-meta {
  color: #8696a0;
}

.theme--dark .file-icon-wrapper {
  background: rgba(255, 255, 255, 0.05);
}

/* Message bubble own files */
.bubble-own .file-attachment {
  background: rgba(0, 0, 0, 0.06);
}

.bubble-own .file-attachment:hover {
  background: rgba(0, 0, 0, 0.1);
}

.theme--dark .bubble-own .file-attachment {
  background: rgba(255, 255, 255, 0.08);
}

.theme--dark .bubble-own .file-attachment:hover {
  background: rgba(255, 255, 255, 0.12);
}

/* Responsive */
@media (max-width: 600px) {
  .file-attachment {
    padding: 8px 10px;
    max-width: 100%;
  }

  .file-icon-wrapper {
    width: 36px;
    height: 36px;
  }

  .file-icon-wrapper .v-icon {
    font-size: 24px !important;
  }

  .file-name {
    font-size: 12px;
  }

  .file-meta {
    font-size: 10px;
  }

  .download-file-btn {
    opacity: 1;
  }
}

/* ===== YOUTUBE PREVIEW ===== */
.youtube-preview {
  background: #1a1a1a;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  margin-bottom: 6px;
  max-width: 400px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.youtube-preview:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.youtube-preview:active {
  transform: scale(0.98);
}

.youtube-thumbnail-wrapper {
  position: relative;
  background: #000;
}

.youtube-thumbnail {
  width: 100%;
  height: 180px;
  background: #000;
}

.youtube-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background: #1a1a1a;
}

.youtube-play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  transition: background 0.3s ease;
}

.youtube-preview:hover .youtube-play-overlay {
  background: rgba(0, 0, 0, 0.1);
}

.play-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(255, 0, 0, 0.8);
  box-shadow: 0 4px 12px rgba(255, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.youtube-preview:hover .play-button {
  transform: scale(1.1);
  background: rgba(255, 0, 0, 0.9);
  box-shadow: 0 6px 20px rgba(255, 0, 0, 0.4);
}

.play-button .v-icon {
  margin-left: 4px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.youtube-duration-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.youtube-info {
  padding: 10px 12px 8px 12px;
  background: #1a1a1a;
}

.youtube-title {
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
}

.youtube-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #aaa;
  flex-wrap: wrap;
}

.youtube-platform {
  color: #FF0000;
  font-weight: 500;
}

.youtube-separator {
  color: #666;
}

.youtube-sender {
  color: #888;
}

.youtube-time {
  color: #888;
}

/* For message bubbles with YouTube */
.bubble-other .youtube-preview {
  background: #1a1a1a;
}

.bubble-own .youtube-preview {
  background: #1a1a1a;
}

.bubble-own .youtube-info {
  background: #1a1a1a;
}

/* Dark mode for YouTube */
.theme--dark .youtube-preview {
  background: #1a1a1a;
}

.theme--dark .youtube-info {
  background: #1a1a1a;
}

/* Responsive */
@media (max-width: 600px) {
  .youtube-preview {
    max-width: 100%;
  }

  .youtube-thumbnail {
    height: 150px;
  }

  .play-button {
    width: 48px;
    height: 48px;
  }

  .play-button .v-icon {
    font-size: 36px !important;
  }

  .youtube-title {
    font-size: 13px;
  }

  .youtube-meta {
    font-size: 11px;
  }
}

@media (max-width: 400px) {
  .youtube-thumbnail {
    height: 120px;
  }

  .play-button {
    width: 40px;
    height: 40px;
  }

  .play-button .v-icon {
    font-size: 28px !important;
  }
}
</style>